import React, { useState, useEffect, useRef } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import { csv } from "d3-fetch";
import "./styles1.css";
import { geoPath } from "d3-geo";
import { mesh } from "topojson-client";
import NewPatientModal from "./NewPatientModal.jsx";
import CountyModal from "./CountyModal.jsx";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

// Lookup object to convert state FIPS codes (first two digits) to state abbreviations.
const fipsToAbbr = {
  "01": "AL",
  "02": "AK",
  "04": "AZ",
  "05": "AR",
  "06": "CA",
  "08": "CO",
  "09": "CT",
  "10": "DE",
  "11": "DC",
  "12": "FL",
  "13": "GA",
  "15": "HI",
  "16": "ID",
  "17": "IL",
  "18": "IN",
  "19": "IA",
  "20": "KS",
  "21": "KY",
  "22": "LA",
  "23": "ME",
  "24": "MD",
  "25": "MA",
  "26": "MI",
  "27": "MN",
  "28": "MS", // Mississippi
  "29": "MO",
  "30": "MT",
  "31": "NE",
  "32": "NV",
  "33": "NH",
  "34": "NJ",
  "35": "NM",
  "36": "NY",
  "37": "NC",
  "38": "ND",
  "39": "OH",
  "40": "OK",
  "41": "OR",
  "42": "PA",
  "44": "RI",
  "45": "SC",
  "46": "SD",
  "47": "TN",
  "48": "TX",
  "49": "UT",
  "50": "VT",
  "51": "VA",
  "53": "WA",
  "54": "WV",
  "55": "WI",
  "56": "WY"
};

// This object defines custom colors for states using their abbreviations.
// Here, Mississippi (MS) is set to red.
// You can add additional mappings (e.g., "AL": "blue", "TX": "green", etc.).
const customStateColors = {
  // Highly landlord-friendly states (Soft Green)
  "AL": "#8BD48B",
  "AR": "#8BD48B",
  "AZ": "#8BD48B",
  "CO": "#8BD48B",
  "FL": "#8BD48B",
  "GA": "#8BD48B",
  "IN": "#8BD48B",
  "KY": "#8BD48B",
  "LA": "#8BD48B",
  "MT": "#8BD48B",
  "NC": "#8BD48B",
  "OH": "#8BD48B",
  "OK": "#8BD48B",
  "TX": "#8BD48B",
  "WV": "#8BD48B",
  "WY": "#8BD48B",

  // Moderately landlord-friendly states (Soft Yellow)
  "AK": "#F7E379",
  "DE": "#F7E379",
  "ID": "#F7E379",
  "IA": "#F7E379",
  "KS": "#F7E379",
  "ME": "#F7E379",
  "MI": "#F7E379",
  "MO": "#F7E379",
  "MS": "#F7E379",
  "NE": "#F7E379",
  "NV": "#F7E379",
  "NH": "#F7E379",
  "NM": "#F7E379",
  "ND": "#F7E379",
  "PA": "#F7E379",
  "SC": "#F7E379",
  "SD": "#F7E379",
  "TN": "#F7E379",
  "UT": "#F7E379",
  "VA": "#F7E379",
  "WI": "#F7E379",

  // Least landlord-friendly states (Soft Red)
  "CA": "#F28C8C",
  "CT": "#F28C8C",
  "DC": "#F28C8C",
  "HI": "#F28C8C",
  "IL": "#F28C8C",
  "MA": "#F28C8C",
  "MD": "#F28C8C",
  "MN": "#F28C8C",
  "NJ": "#F28C8C",
  "NY": "#F28C8C",
  "OR": "#F28C8C",
  "RI": "#F28C8C",
  "VT": "#F28C8C",
  "WA": "#F28C8C"
};




const MapChart = ({ setTooltipContent }) => {
  const [data, setData] = useState([]);
  const [topology, setTopology] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedCountyID, setSelectedCountyID] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const dropdownRef = useRef(null);
  const countiesData = [
    { id: "01001", name: "Autauga County, AL" },
    { id: "01003", name: "Baldwin County, AL" },
    { id: "01005", name: "Barbour County, AL" },
    { id: "01007", name: "Bibb County, AL" },
    { id: "01009", name: "Blount County, AL" },
    { id: "01011", name: "Bullock County, AL" },
    { id: "01013", name: "Butler County, AL" },
    { id: "01015", name: "Calhoun County, AL" },
    { id: "01017", name: "Chambers County, AL" },
    { id: "01019", name: "Cherokee County, AL" },
    { id: "01021", name: "Chilton County, AL" },
    { id: "01023", name: "Choctaw County, AL" },
    { id: "01025", name: "Clarke County, AL" },
    { id: "01027", name: "Clay County, AL" },
    { id: "01029", name: "Cleburne County, AL" },
    { id: "01031", name: "Coffee County, AL" },
    { id: "01033", name: "Colbert County, AL" },
    { id: "01035", name: "Conecuh County, AL" },
    { id: "01037", name: "Coosa County, AL" },
    { id: "01039", name: "Covington County, AL" },
    { id: "01041", name: "Crenshaw County, AL" },
    { id: "01043", name: "Cullman County, AL" },
    { id: "01045", name: "Dale County, AL" },
    { id: "01047", name: "Dallas County, AL" },
    { id: "01049", name: "DeKalb County, AL" },
    { id: "01051", name: "Elmore County, AL" },
    { id: "01053", name: "Escambia County, AL" },
    { id: "01055", name: "Etowah County, AL" },
    { id: "01057", name: "Fayette County, AL" },
    { id: "01059", name: "Franklin County, AL" },
    { id: "01061", name: "Geneva County, AL" },
    { id: "01063", name: "Greene County, AL" },
    { id: "01065", name: "Hale County, AL" },
    { id: "01067", name: "Henry County, AL" },
    { id: "01069", name: "Houston County, AL" },
    { id: "01071", name: "Jackson County, AL" },
    { id: "01073", name: "Jefferson County, AL" },
    { id: "01075", name: "Lamar County, AL" },
    { id: "01077", name: "Lauderdale County, AL" },
    { id: "01079", name: "Lawrence County, AL" },
    { id: "01081", name: "Lee County, AL" },
    { id: "01083", name: "Limestone County, AL" },
    { id: "01085", name: "Lowndes County, AL" },
    { id: "01087", name: "Macon County, AL" },
    { id: "01089", name: "Madison County, AL" },
    { id: "01091", name: "Marengo County, AL" },
    { id: "01093", name: "Marion County, AL" },
    { id: "01095", name: "Marshall County, AL" },
    { id: "01097", name: "Mobile County, AL" },
    { id: "01099", name: "Monroe County, AL" },
    { id: "01101", name: "Montgomery County, AL" },
    { id: "01103", name: "Morgan County, AL" },
    { id: "01105", name: "Perry County, AL" },
    { id: "01107", name: "Pickens County, AL" },
    { id: "01109", name: "Pike County, AL" },
    { id: "01111", name: "Randolph County, AL" },
    { id: "01113", name: "Russell County, AL" },
    { id: "01115", name: "St. Clair County, AL" },
    { id: "01117", name: "Shelby County, AL" },
    { id: "01119", name: "Sumter County, AL" },
    { id: "01121", name: "Talladega County, AL" },
    { id: "01123", name: "Tallapoosa County, AL" },
    { id: "01125", name: "Tuscaloosa County, AL" },
    { id: "01127", name: "Walker County, AL" },
    { id: "01129", name: "Washington County, AL" },
    { id: "01131", name: "Wilcox County, AL" },
    { id: "01133", name: "Winston County, AL" },
    { id: "02013", name: "Aleutians East Borough, AK" },
  { id: "02016", name: "Aleutians West Census Area, AK" },
  { id: "02020", name: "Anchorage Borough/municipality, AK" },
  { id: "02050", name: "Bethel Census Area, AK" },
  { id: "02060", name: "Bristol Bay Borough, AK" },
  { id: "02068", name: "Denali Borough, AK" },
  { id: "02070", name: "Dillingham Census Area, AK" },
  { id: "02090", name: "Fairbanks North Star Borough, AK" },
  { id: "02100", name: "Haines Borough, AK" },
  { id: "02105", name: "Hoonah-Angoon Census Area, AK" },
  { id: "02110", name: "Juneau Borough/city, AK" },
  { id: "02122", name: "Kenai Peninsula Borough, AK" },
  { id: "02130", name: "Ketchikan Gateway Borough, AK" },
  { id: "02150", name: "Kodiak Island Borough, AK" },
  { id: "02158", name: "Kusilvak Census Area, AK" },
  { id: "02164", name: "Lake and Peninsula Borough, AK" },
  { id: "02170", name: "Matanuska-Susitna Borough, AK" },
  { id: "02180", name: "Nome Census Area, AK" },
  { id: "02185", name: "North Slope Borough, AK" },
  { id: "02188", name: "Northwest Arctic Borough, AK" },
  { id: "02195", name: "Petersburg Borough, AK" },
  { id: "02198", name: "Prince of Wales-Hyder Census Area, AK" },
  { id: "02220", name: "Sitka Borough/city, AK" },
  { id: "02230", name: "Skagway Municipality, AK" },
  { id: "02240", name: "Southeast Fairbanks Census Area, AK" },
  { id: "02261", name: "Valdez-Cordova Census Area, AK" },
  { id: "02275", name: "Wrangell Borough/city, AK" },
  { id: "02282", name: "Yakutat Borough/city, AK" },
  { id: "02290", name: "Yukon-Koyukuk Census Area, AK" },
  { id: "04001", name: "Apache County, AZ" },
  { id: "04003", name: "Cochise County, AZ" },
  { id: "04005", name: "Coconino County, AZ" },
  { id: "04007", name: "Gila County, AZ" },
  { id: "04009", name: "Graham County, AZ" },
  { id: "04011", name: "Greenlee County, AZ" },
  { id: "04012", name: "La Paz County, AZ" },
  { id: "04013", name: "Maricopa County, AZ" },
  { id: "04015", name: "Mohave County, AZ" },
  { id: "04017", name: "Navajo County, AZ" },
  { id: "04019", name: "Pima County, AZ" },
  { id: "04021", name: "Pinal County, AZ" },
  { id: "04023", name: "Santa Cruz County, AZ" },
  { id: "04025", name: "Yavapai County, AZ" },
  { id: "04027", name: "Yuma County, AZ" },
  { id: "05001", name: "Arkansas County, AR" },
  { id: "05003", name: "Ashley County, AR" },
  { id: "05005", name: "Baxter County, AR" },
  { id: "05007", name: "Benton County, AR" },
  { id: "05009", name: "Boone County, AR" },
  { id: "05011", name: "Bradley County, AR" },
  { id: "05013", name: "Calhoun County, AR" },
  { id: "05015", name: "Carroll County, AR" },
  { id: "05017", name: "Chicot County, AR" },
  { id: "05019", name: "Clark County, AR" },
  { id: "05021", name: "Clay County, AR" },
  { id: "05023", name: "Cleburne County, AR" },
  { id: "05025", name: "Cleveland County, AR" },
  { id: "05027", name: "Columbia County, AR" },
  { id: "05029", name: "Conway County, AR" },
  { id: "05031", name: "Craighead County, AR" },
  { id: "05033", name: "Crawford County, AR" },
  { id: "05035", name: "Crittenden County, AR" },
  { id: "05037", name: "Cross County, AR" },
  { id: "05039", name: "Dallas County, AR" },
  { id: "05041", name: "Desha County, AR" },
  { id: "05043", name: "Drew County, AR" },
  { id: "05045", name: "Faulkner County, AR" },
  { id: "05047", name: "Franklin County, AR" },
  { id: "05049", name: "Fulton County, AR" },
  { id: "05051", name: "Garland County, AR" },
  { id: "05053", name: "Grant County, AR" },
  { id: "05055", name: "Greene County, AR" },
  { id: "05057", name: "Hempstead County, AR" },
  { id: "05059", name: "Hot Spring County, AR" },
  { id: "05061", name: "Howard County, AR" },
  { id: "05063", name: "Independence County, AR" },
  { id: "05065", name: "Izard County, AR" },
  { id: "05067", name: "Jackson County, AR" },
  { id: "05069", name: "Jefferson County, AR" },
  { id: "05071", name: "Johnson County, AR" },
  { id: "05073", name: "Lafayette County, AR" },
  { id: "05075", name: "Lawrence County, AR" },
  { id: "05077", name: "Lee County, AR" },
  { id: "05079", name: "Lincoln County, AR" },
  { id: "05081", name: "Little River County, AR" },
  { id: "05083", name: "Logan County, AR" },
  { id: "05085", name: "Lonoke County, AR" },
  { id: "05087", name: "Madison County, AR" },
  { id: "05089", name: "Marion County, AR" },
  { id: "05091", name: "Miller County, AR" },
  { id: "05093", name: "Mississippi County, AR" },
  { id: "05095", name: "Monroe County, AR" },
  { id: "05097", name: "Montgomery County, AR" },
  { id: "05099", name: "Nevada County, AR" },
  { id: "05101", name: "Newton County, AR" },
  { id: "05103", name: "Ouachita County, AR" },
  { id: "05105", name: "Perry County, AR" },
  { id: "05107", name: "Phillips County, AR" },
  { id: "05109", name: "Pike County, AR" },
  { id: "05111", name: "Poinsett County, AR" },
  { id: "05113", name: "Polk County, AR" },
  { id: "05115", name: "Pope County, AR" },
  { id: "05117", name: "Prairie County, AR" },
  { id: "05119", name: "Pulaski County, AR" },
  { id: "05121", name: "Randolph County, AR" },
  { id: "05123", name: "St. Francis County, AR" },
  { id: "05125", name: "Saline County, AR" },
  { id: "05127", name: "Scott County, AR" },
  { id: "05129", name: "Searcy County, AR" },
  { id: "05131", name: "Sebastian County, AR" },
  { id: "05133", name: "Sevier County, AR" },
  { id: "05135", name: "Sharp County, AR" },
  { id: "05137", name: "Stone County, AR" },
  { id: "05139", name: "Union County, AR" },
  { id: "05141", name: "Van Buren County, AR" },
  { id: "05143", name: "Washington County, AR" },
  { id: "05145", name: "White County, AR" },
  { id: "05147", name: "Woodruff County, AR" },
  { id: "05149", name: "Yell County, AR" },
  { id: "06001", name: "Alameda County, CA" },
  { id: "06003", name: "Alpine County, CA" },
  { id: "06005", name: "Amador County, CA" },
  { id: "06007", name: "Butte County, CA" },
  { id: "06009", name: "Calaveras County, CA" },
  { id: "06011", name: "Colusa County, CA" },
  { id: "06013", name: "Contra Costa County, CA" },
  { id: "06015", name: "Del Norte County, CA" },
  { id: "06017", name: "El Dorado County, CA" },
  { id: "06019", name: "Fresno County, CA" },
  { id: "06021", name: "Glenn County, CA" },
  { id: "06023", name: "Humboldt County, CA" },
  { id: "06025", name: "Imperial County, CA" },
  { id: "06027", name: "Inyo County, CA" },
  { id: "06029", name: "Kern County, CA" },
  { id: "06031", name: "Kings County, CA" },
  { id: "06033", name: "Lake County, CA" },
  { id: "06035", name: "Lassen County, CA" },
  { id: "06037", name: "Los Angeles County, CA" },
  { id: "06039", name: "Madera County, CA" },
  { id: "06041", name: "Marin County, CA" },
  { id: "06043", name: "Mariposa County, CA" },
  { id: "06045", name: "Mendocino County, CA" },
  { id: "06047", name: "Merced County, CA" },
  { id: "06049", name: "Modoc County, CA" },
  { id: "06051", name: "Mono County, CA" },
  { id: "06053", name: "Monterey County, CA" },
  { id: "06055", name: "Napa County, CA" },
  { id: "06057", name: "Nevada County, CA" },
  { id: "06059", name: "Orange County, CA" },
  { id: "06061", name: "Placer County, CA" },
  { id: "06063", name: "Plumas County, CA" },
  { id: "06065", name: "Riverside County, CA" },
  { id: "06067", name: "Sacramento County, CA" },
  { id: "06069", name: "San Benito County, CA" },
  { id: "06071", name: "San Bernardino County, CA" },
  { id: "06073", name: "San Diego County, CA" },
  { id: "06075", name: "San Francisco County, CA" },
  { id: "06077", name: "San Joaquin County, CA" },
  { id: "06079", name: "San Luis Obispo County, CA" },
  { id: "06081", name: "San Mateo County, CA" },
  { id: "06083", name: "Santa Barbara County, CA" },
  { id: "06085", name: "Santa Clara County, CA" },
  { id: "06087", name: "Santa Cruz County, CA" },
  { id: "06089", name: "Shasta County, CA" },
  { id: "06091", name: "Sierra County, CA" },
  { id: "06093", name: "Siskiyou County, CA" },
  { id: "06095", name: "Solano County, CA" },
  { id: "06097", name: "Sonoma County, CA" },
  { id: "06099", name: "Stanislaus County, CA" },
  { id: "06101", name: "Sutter County, CA" },
  { id: "06103", name: "Tehama County, CA" },
  { id: "06105", name: "Trinity County, CA" },
  { id: "06107", name: "Tulare County, CA" },
  { id: "06109", name: "Tuolumne County, CA" },
  { id: "06111", name: "Ventura County, CA" },
  { id: "06113", name: "Yolo County, CA" },
  { id: "06115", name: "Yuba County, CA" },
  { id: "08001", name: "Adams County, CO" },
  { id: "08003", name: "Alamosa County, CO" },
  { id: "08005", name: "Arapahoe County, CO" },
  { id: "08007", name: "Archuleta County, CO" },
  { id: "08009", name: "Baca County, CO" },
  { id: "08011", name: "Bent County, CO" },
  { id: "08013", name: "Boulder County, CO" },
  { id: "08014", name: "Broomfield County/city, CO" },
  { id: "08015", name: "Chaffee County, CO" },
  { id: "08017", name: "Cheyenne County, CO" },
  { id: "08019", name: "Clear Creek County, CO" },
  { id: "08021", name: "Conejos County, CO" },
  { id: "08023", name: "Costilla County, CO" },
  { id: "08025", name: "Crowley County, CO" },
  { id: "08027", name: "Custer County, CO" },
  { id: "08029", name: "Delta County, CO" },
  { id: "08031", name: "Denver County/city, CO" },
  { id: "08033", name: "Dolores County, CO" },
  { id: "08035", name: "Douglas County, CO" },
  { id: "08037", name: "Eagle County, CO" },
  { id: "08039", name: "Elbert County, CO" },
  { id: "08041", name: "El Paso County, CO" },
  { id: "08043", name: "Fremont County, CO" },
  { id: "08045", name: "Garfield County, CO" },
  { id: "08047", name: "Gilpin County, CO" },
  { id: "08049", name: "Grand County, CO" },
  { id: "08051", name: "Gunnison County, CO" },
  { id: "08053", name: "Hinsdale County, CO" },
  { id: "08055", name: "Huerfano County, CO" },
  { id: "08057", name: "Jackson County, CO" },
  { id: "08059", name: "Jefferson County, CO" },
  { id: "08061", name: "Kiowa County, CO" },
  { id: "08063", name: "Kit Carson County, CO" },
  { id: "08065", name: "Lake County, CO" },
  { id: "08067", name: "La Plata County, CO" },
  { id: "08069", name: "Larimer County, CO" },
  { id: "08071", name: "Las Animas County, CO" },
  { id: "08073", name: "Lincoln County, CO" },
  { id: "08075", name: "Logan County, CO" },
  { id: "08077", name: "Mesa County, CO" },
  { id: "08079", name: "Mineral County, CO" },
  { id: "08081", name: "Moffat County, CO" },
  { id: "08083", name: "Montezuma County, CO" },
  { id: "08085", name: "Montrose County, CO" },
  { id: "08087", name: "Morgan County, CO" },
  { id: "08089", name: "Otero County, CO" },
  { id: "08091", name: "Ouray County, CO" },
  { id: "08093", name: "Park County, CO" },
  { id: "08095", name: "Phillips County, CO" },
  { id: "08097", name: "Pitkin County, CO" },
  { id: "08099", name: "Prowers County, CO" },
  { id: "08101", name: "Pueblo County, CO" },
  { id: "08103", name: "Rio Blanco County, CO" },
  { id: "08105", name: "Rio Grande County, CO" },
  { id: "08107", name: "Routt County, CO" },
  { id: "08109", name: "Saguache County, CO" },
  { id: "08111", name: "San Juan County, CO" },
  { id: "08113", name: "San Miguel County, CO" },
  { id: "08115", name: "Sedgwick County, CO" },
  { id: "08117", name: "Summit County, CO" },
  { id: "08119", name: "Teller County, CO" },
  { id: "08121", name: "Washington County, CO" },
  { id: "08123", name: "Weld County, CO" },
  { id: "08125", name: "Yuma County, CO" },
  { id: "09001", name: "Fairfield County, CT" },
  { id: "09003", name: "Hartford County, CT" },
  { id: "09005", name: "Litchfield County, CT" },
  { id: "09007", name: "Middlesex County, CT" },
  { id: "09009", name: "New Haven County, CT" },
  { id: "09011", name: "New London County, CT" },
  { id: "09013", name: "Tolland County, CT" },
  { id: "09015", name: "Windham County, CT" },
  { id: "10001", name: "Kent County, DE" },
  { id: "10003", name: "New Castle County, DE" },
  { id: "10005", name: "Sussex County, DE" },
  { id: "11001", name: "District of Columbia" },
  { id: "12001", name: "Alachua County, FL" },
  { id: "12003", name: "Baker County, FL" },
  { id: "12005", name: "Bay County, FL" },
  { id: "12007", name: "Bradford County, FL" },
  { id: "12009", name: "Brevard County, FL" },
  { id: "12011", name: "Broward County, FL" },
  { id: "12013", name: "Calhoun County, FL" },
  { id: "12015", name: "Charlotte County, FL" },
  { id: "12017", name: "Citrus County, FL" },
  { id: "12019", name: "Clay County, FL" },
  { id: "12021", name: "Collier County, FL" },
  { id: "12023", name: "Columbia County, FL" },
  { id: "12027", name: "DeSoto County, FL" },
  { id: "12029", name: "Dixie County, FL" },
  { id: "12031", name: "Duval County, FL" },
  { id: "12033", name: "Escambia County, FL" },
  { id: "12035", name: "Flagler County, FL" },
  { id: "12037", name: "Franklin County, FL" },
  { id: "12039", name: "Gadsden County, FL" },
  { id: "12041", name: "Gilchrist County, FL" },
  { id: "12043", name: "Glades County, FL" },
  { id: "12045", name: "Gulf County, FL" },
  { id: "12047", name: "Hamilton County, FL" },
  { id: "12049", name: "Hardee County, FL" },
  { id: "12051", name: "Hendry County, FL" },
  { id: "12053", name: "Hernando County, FL" },
  { id: "12055", name: "Highlands County, FL" },
  { id: "12057", name: "Hillsborough County, FL" },
  { id: "12059", name: "Holmes County, FL" },
  { id: "12061", name: "Indian River County, FL" },
  { id: "12063", name: "Jackson County, FL" },
  { id: "12065", name: "Jefferson County, FL" },
  { id: "12067", name: "Lafayette County, FL" },
  { id: "12069", name: "Lake County, FL" },
  { id: "12071", name: "Lee County, FL" },
  { id: "12073", name: "Leon County, FL" },
  { id: "12075", name: "Levy County, FL" },
  { id: "12077", name: "Liberty County, FL" },
  { id: "12079", name: "Madison County, FL" },
  { id: 12081, name: "Manatee County, FL" },
  { id: 12083, name: "Marion County, FL" },
  { id: 12085, name: "Martin County, FL" },
  { id: 12086, name: "Miami-Dade County, FL" },
  { id: 12087, name: "Monroe County, FL" },
  { id: 12089, name: "Nassau County, FL" },
  { id: 12091, name: "Okaloosa County, FL" },
  { id: 12093, name: "Okeechobee County, FL" },
  { id: 12095, name: "Orange County, FL" },
  { id: 12097, name: "Osceola County, FL" },
  { id: 12099, name: "Palm Beach County, FL" },
  { id: 12101, name: "Pasco County, FL" },
  { id: 12103, name: "Pinellas County, FL" },
  { id: 12105, name: "Polk County, FL" },
  { id: 12107, name: "Putnam County, FL" },
  { id: 12109, name: "St. Johns County, FL" },
  { id: 12111, name: "St. Lucie County, FL" },
  { id: 12113, name: "Santa Rosa County, FL" },
  { id: 12115, name: "Sarasota County, FL" },
  { id: 12117, name: "Seminole County, FL" },
  { id: 12119, name: "Sumter County, FL" },
  { id: 12121, name: "Suwannee County, FL" },
  { id: 12123, name: "Taylor County, FL" },
  { id: 12125, name: "Union County, FL" },
  { id: 12127, name: "Volusia County, FL" },
  { id: 12129, name: "Wakulla County, FL" },
  { id: 12131, name: "Walton County, FL" },
  { id: 12133, name: "Washington County, FL" },
  { id: 13001, name: "Appling County, GA" },
  { id: 13003, name: "Atkinson County, GA" },
  { id: 13005, name: "Bacon County, GA" },
  { id: 13007, name: "Baker County, GA" },
  { id: 13009, name: "Balloon County, GA" },
  { id: 13011, name: "Berrien County, GA" },
  { id: 13013, name: "Bib County, GA" },
  { id: 13015, name: "Brantley County, GA" },
  { id: 13017, name: "Brooks County, GA" },
  { id: 13019, name: "Burke County, GA" },
  { id: 13021, name: "Butts County, GA" },
  { id: 13023, name: "Calhoun County, GA" },
  { id: 13025, name: "Camden County, GA" },
  { id: 13027, name: "Candler County, GA" },
  { id: 13029, name: "Carroll County, GA" },
  { id: 13031, name: "Catoosa County, GA" },
  { id: 13033, name: "Charlton County, GA" },
  { id: 13035, name: "Chatham County, GA" },
  { id: 13037, name: "Chattahoochee County, GA" },
  { id: 13039, name: "Chattooga County, GA" },
  { id: 13041, name: "Cherokee County, GA" },
  { id: 13043, name: "Clarke County, GA" },
  { id: 13045, name: "Clay County, GA" },
  { id: 13047, name: "Clayton County, GA" },
  { id: 13049, name: "Clinch County, GA" },
  { id: 13051, name: "Cobb County, GA" },
  { id: 13053, name: "Coffee County, GA" },
  { id: 13055, name: "Colquitt County, GA" },
  { id: 13057, name: "Columbia County, GA" },
  { id: 13059, name: "Cook County, GA" },
  { id: 13061, name: "Coweta County, GA" },
  { id: 13063, name: "Crawford County, GA" },
  { id: 13065, name: "Crisp County, GA" },
  { id: 13067, name: "Dade County, GA" },
  { id: 13069, name: "Dawson County, GA" },
  { id: 13071, name: "DeKalb County, GA" },
  { id: 13073, name: "Dodge County, GA" },
  { id: 13075, name: "Dooly County, GA" },
  { id: 13077, name: "Dougherty County, GA" },
  { id: 13079, name: "Douglas County, GA" },
  { id: 13081, name: "Early County, GA" },
  { id: 13083, name: "Echols County, GA" },
  { id: 13085, name: "Effingham County, GA" },
  { id: 13087, name: "Elbert County, GA" },
  { id: 13089, name: "Emanuel County, GA" },
  { id: 13091, name: "Evans County, GA" },
  { id: 13093, name: "Fannin County, GA" },
  { id: 13095, name: "Fayette County, GA" },
  { id: 13097, name: "Floyd County, GA" },
  { id: 13099, name: "Forsyth County, GA" },
  { id: 13101, name: "Franklin County, GA" },
  { id: 13103, name: "Fulton County, GA" },
  { id: 13105, name: "Gilmer County, GA" },
  { id: 13107, name: "Glascock County, GA" },
  { id: 13109, name: "Glynn County, GA" },
  { id: 13111, name: "Greene County, GA" },
  { id: 13113, name: "Gwinnett County, GA" },
  { id: 13115, name: "Habersham County, GA" },
  { id: 13117, name: "Hall County, GA" },
  { id: 13119, name: "Hancock County, GA" },
  { id: 13121, name: "Haralson County, GA" },
  { id: 13123, name: "Harris County, GA" },
  { id: 13125, name: "Hart County, GA" },
  { id: 13127, name: "Heard County, GA" },
  { id: 13129, name: "Henry County, GA" },
  { id: 13131, name: "Houston County, GA" },
  { id: 13133, name: "Irwin County, GA" },
  { id: 13135, name: "Jackson County, GA" },
  { id: 13137, name: "Jasper County, GA" },
  { id: 13139, name: "Jeff Davis County, GA" },
  { id: 13141, name: "Jefferson County, GA" },
  { id: 13143, name: "Jenkins County, GA" },
  { id: 13145, name: "Johnson County, GA" },
  { id: 13147, name: "Jones County, GA" },
  { id: 13149, name: "Lamar County, GA" },
  { id: 13151, name: "Lanier County, GA" },
  { id: 13153, name: "Laurens County, GA" },
  { id: 13155, name: "Lee County, GA" },
  { id: 13157, name: "Liberty County, GA" },
  { id: 13159, name: "Lincoln County, GA" },
  { id: 13161, name: "Long County, GA" },
  { id: 13163, name: "Lowndes County, GA" },
  { id: 13165, name: "Lumpkin County, GA" },
  { id: 13167, name: "McDuffie County, GA" },
  { id: 13169, name: "McIntosh County, GA" },
  { id: 13171, name: "Macon County, GA" },
  { id: 13173, name: "Madison County, GA" },
  { id: 13175, name: "Marion County, GA" },
  { id: 13177, name: "Meriwether County, GA" },
  { id: 13179, name: "Miller County, GA" },
  { id: 13181, name: "Mitchell County, GA" },
  { id: 13183, name: "Monroe County, GA" },
  { id: 13185, name: "Montgomery County, GA" },
  { id: 13187, name: "Morgan County, GA" },
  { id: 13189, name: "Murray County, GA" },
  { id: 13191, name: "Muscogee County, GA" },
  { id: 13193, name: "Newton County, GA" },
  { id: 13195, name: "Oconee County, GA" },
  { id: 13197, name: "Oglethorpe County, GA" },
  { id: 13199, name: "Paulding County, GA" },
  { id: 13201, name: "Peach County, GA" },
  { id: 13203, name: "Pickens County, GA" },
  { id: 13205, name: "Pierce County, GA" },
  { id: 13207, name: "Pike County, GA" },
  { id: 13209, name: "Polk County, GA" },
  { id: 13211, name: "Pulaski County, GA" },
  { id: 13213, name: "Putnam County, GA" },
  { id: 13215, name: "Quitman County, GA" },
  { id: 13217, name: "Rabun County, GA" },
  { id: 13219, name: "Randolph County, GA" },
  { id: 13221, name: "Richmond County, GA" },
  { id: 13223, name: "Rockdale County, GA" },
  { id: 13225, name: "Schley County, GA" },
  { id: 13227, name: "Screven County, GA" },
  { id: 13229, name: "Spalding County, GA" },
  { id: 13231, name: "Stephens County, GA" },
  { id: 13233, name: "Stewart County, GA" },
  { id: 13235, name: "Sumter County, GA" },
  { id: 13237, name: "Talbot County, GA" },
  { id: 13239, name: "Taliaferro County, GA" },
  { id: 13241, name: "Tattnall County, GA" },
  { id: 13243, name: "Taylor County, GA" },
  { id: 13245, name: "Telfair County, GA" },
  { id: 13247, name: "Terrell County, GA" },
  { id: 13249, name: "Thomas County, GA" },
  { id: 13251, name: "Tift County, GA" },
  { id: 13253, name: "Toombs County, GA" },
  { id: 13255, name: "Towns County, GA" },
  { id: 13257, name: "Treutlen County, GA" },
  { id: 13259, name: "Troup County, GA" },
  { id: 13261, name: "Turner County, GA" },
  { id: 13263, name: "Twiggs County, GA" },
  { id: 13265, name: "Union County, GA" },
  { id: 13267, name: "Upson County, GA" },
  { id: 13269, name: "Walker County, GA" },
  { id: 13271, name: "Walton County, GA" },
  { id: 13273, name: "Ware County, GA" },
  { id: 13275, name: "Warren County, GA" },
  { id: 13277, name: "Washington County, GA" },
  { id: 13279, name: "Wayne County, GA" },
  { id: 13281, name: "Webster County, GA" },
  { id: 13283, name: "Wheeler County, GA" },
  { id: 13285, name: "White County, GA" },
  { id: 13287, name: "Whitfield County, GA" },
  { id: 13289, name: "Wilcox County, GA" },
  { id: 13291, name: "Wilkes County, GA" },
  { id: 13293, name: "Wilson County, GA" },
  { id: 13295, name: "Worth County, GA" },
  { id: 15001, name: "Hawaii County, HI" },
  { id: 15003, name: "Honolulu County/city, HI" },
  { id: 15007, name: "Kauai County, HI" },
  { id: 15009, name: "Maui County, HI" },
  { id: 16001, name: "Ada County, ID" },
  { id: 16003, name: "Adams County, ID" },
  { id: 16005, name: "Bannock County, ID" },
  { id: 16007, name: "Bear Lake County, ID" },
  { id: 16009, name: "Benewah County, ID" },
  { id: 16011, name: "Bingham County, ID" },
  { id: 16013, name: "Blaine County, ID" },
  { id: 16015, name: "Boise County, ID" },
  { id: 16017, name: "Bonner County, ID" },
  { id: 16019, name: "Bonneville County, ID" },
  { id: 16021, name: "Boundary County, ID" },
  { id: 16023, name: "Butte County, ID" },
  { id: 16025, name: "Camas County, ID" },
  { id: 16027, name: "Canyon County, ID" },
  { id: 16029, name: "Caribou County, ID" },
  { id: 16031, name: "Cassia County, ID" },
  { id: 16033, name: "Clark County, ID" },
  { id: 16035, name: "Clearwater County, ID" },
  { id: 16037, name: "Custer County, ID" },
  { id: 16039, name: "Elmore County, ID" },
  { id: 16041, name: "Franklin County, ID" },
  { id: 16043, name: "Fremont County, ID" },
  { id: 16045, name: "Gem County, ID" },
  { id: 16047, name: "Gooding County, ID" },
  { id: 16049, name: "Idaho County, ID" },
  { id: 16051, name: "Jefferson County, ID" },
  { id: 16053, name: "Jerome County, ID" },
  { id: 16055, name: "Kootenai County, ID" },
  { id: 16057, name: "Latah County, ID" },
  { id: 16059, name: "Lemhi County, ID" },
  { id: 16061, name: "Lewis County, ID" },
  { id: 16063, name: "Lincoln County, ID" },
  { id: 16065, name: "Madison County, ID" },
  { id: 16067, name: "Minidoka County, ID" },
  { id: 16069, name: "Nez Perce County, ID" },
  { id: 16071, name: "Oneida County, ID" },
  { id: 16073, name: "Owyhee County, ID" },
  { id: 16075, name: "Payette County, ID" },
  { id: 16077, name: "Power County, ID" },
  { id: 16079, name: "Shoshone County, ID" },
  { id: 16081, name: "Teton County, ID" },
  { id: 16083, name: "Twin Falls County, ID" },
  { id: 16085, name: "Valley County, ID" },
  { id: 16087, name: "Washington County, ID" },
  { id: 17001, name: "Adams County, IL" },
  { id: 17003, name: "Alexander County, IL" },
  { id: 17005, name: "Bond County, IL" },
  { id: 17007, name: "Boone County, IL" },
  { id: 17009, name: "Brown County, IL" },
  { id: 17011, name: "Bureau County, IL" },
  { id: 17013, name: "Calhoun County, IL" },
  { id: 17015, name: "Carroll County, IL" },
  { id: 17017, name: "Cass County, IL" },
  { id: 17019, name: "Champaign County, IL" },
  { id: 17021, name: "Christian County, IL" },
  { id: 17023, name: "Clark County, IL" },
  { id: 17025, name: "Clay County, IL" },
  { id: 17027, name: "Clinton County, IL" },
  { id: 17029, name: "Coles County, IL" },
  { id: 17031, name: "Cook County, IL" },
  { id: 17033, name: "Crawford County, IL" },
  { id: 17035, name: "Cumberland County, IL" },
  { id: 17037, name: "DeKalb County, IL" },
  { id: 17039, name: "De Witt County, IL" },
  { id: 17041, name: "Douglas County, IL" },
  { id: 17043, name: "DuPage County, IL" },
  { id: 17045, name: "Edgar County, IL" },
  { id: 17047, name: "Edwards County, IL" },
  { id: 17049, name: "Effingham County, IL" },
  { id: 17051, name: "Fayette County, IL" },
  { id: 17053, name: "Ford County, IL" },
  { id: 17055, name: "Franklin County, IL" },
  { id: 17057, name: "Fulton County, IL" },
  { id: 17059, name: "Gallatin County, IL" },
  { id: 17061, name: "Greene County, IL" },
  { id: 17063, name: "Grundy County, IL" },
  { id: 17065, name: "Hamilton County, IL" },
  { id: 17067, name: "Hancock County, IL" },
  { id: 17069, name: "Hardin County, IL" },
  { id: 17071, name: "Henderson County, IL" },
  { id: 17073, name: "Henry County, IL" },
  { id: 17075, name: "Iroquois County, IL" },
  { id: 17077, name: "Jackson County, IL" },
  { id: 17079, name: "Jasper County, IL" },
  { id: 17081, name: "Jefferson County, IL" },
  { id: 17083, name: "Jersey County, IL" },
  { id: 17085, name: "Jo Daviess County, IL" },
  { id: 17087, name: "Johnson County, IL" },
  { id: 17089, name: "Kane County, IL" },
  { id: 17091, name: "Kankakee County, IL" },
  { id: 17093, name: "Kendall County, IL" },
  { id: 17095, name: "Knox County, IL" },
  { id: 17097, name: "Lake County, IL" },
  { id: 17099, name: "LaSalle County, IL" },
  { id: 17101, name: "Lawrence County, IL" },
  { id: 17103, name: "Lee County, IL" },
  { id: 17105, name: "Livingston County, IL" },
  { id: 17107, name: "Logan County, IL" },
  { id: 17109, name: "McDonough County, IL" },
  { id: 17111, name: "McHenry County, IL" },
  { id: 17113, name: "McLean County, IL" },
  { id: 17115, name: "Macon County, IL" },
  { id: 17117, name: "Macoupin County, IL" },
  { id: 17119, name: "Madison County, IL" },
  { id: 17121, name: "Marion County, IL" },
  { id: 17123, name: "Marshall County, IL" },
  { id: 17125, name: "Mason County, IL" },
  { id: 17127, name: "Massac County, IL" },
  { id: 17129, name: "Menard County, IL" },
  { id: 17131, name: "Mercer County, IL" },
  { id: 17133, name: "Monroe County, IL" },
  { id: 17135, name: "Montgomery County, IL" },
  { id: 17137, name: "Morgan County, IL" },
  { id: 17139, name: "Moultrie County, IL" },
  { id: 17141, name: "Ogle County, IL" },
  { id: 17143, name: "Peoria County, IL" },
  { id: 17145, name: "Perry County, IL" },
  { id: 17147, name: "Piatt County, IL" },
  { id: 17149, name: "Pike County, IL" },
  { id: 17151, name: "Pope County, IL" },
  { id: 17153, name: "Pulaski County, IL" },
  { id: 17155, name: "Putnam County, IL" },
  { id: 17157, name: "Randolph County, IL" },
  { id: 17159, name: "Richland County, IL" },
  { id: 17161, name: "Rock Island County, IL" },
  { id: 17163, name: "St. Clair County, IL" },
  { id: 17165, name: "Saline County, IL" },
  { id: 17167, name: "Sangamon County, IL" },
  { id: 17169, name: "Schuyler County, IL" },
  { id: 17171, name: "Scott County, IL" },
  { id: 17173, name: "Shelby County, IL" },
  { id: 17175, name: "Stark County, IL" },
  { id: 17177, name: "Stephenson County, IL" },
  { id: 17179, name: "Tazewell County, IL" },
  { id: 17181, name: "Union County, IL" },
  { id: 17183, name: "Vermilion County, IL" },
  { id: 17185, name: "Wabash County, IL" },
  { id: 17187, name: "Warren County, IL" },
  { id: 17189, name: "Washington County, IL" },
  { id: 17191, name: "Wayne County, IL" },
  { id: 17193, name: "White County, IL" },
  { id: 17195, name: "Whiteside County, IL" },
  { id: 17197, name: "Will County, IL" },
  { id: 17199, name: "Williamson County, IL" },
  { id: 17201, name: "Winnebago County, IL" },
  { id: 17203, name: "Woodford County, IL" },
  { id: 18001, name: "Adams County, IN" },
  { id: 18003, name: "Allen County, IN" },
  { id: 18005, name: "Bartholomew County, IN" },
  { id: 18007, name: "Benton County, IN" },
  { id: 18009, name: "Blackford County, IN" },
  { id: 18011, name: "Boone County, IN" },
  { id: 18013, name: "Brown County, IN" },
  { id: 18015, name: "Carroll County, IN" },
  { id: 18017, name: "Cass County, IN" },
  { id: 18019, name: "Clark County, IN" },
  { id: 18021, name: "Clay County, IN" },
  { id: 18023, name: "Clinton County, IN" },
  { id: 18025, name: "Crawford County, IN" },
  { id: 18027, name: "Daviess County, IN" },
  { id: 18029, name: "Dearborn County, IN" },
  { id: 18031, name: "Decatur County, IN" },
  { id: 18033, name: "DeKalb County, IN" },
  { id: 18035, name: "Delaware County, IN" },
  { id: 18037, name: "Dubois County, IN" },
  { id: 18039, name: "Elkhart County, IN" },
  { id: 18041, name: "Fayette County, IN" },
  { id: 18043, name: "Floyd County, IN" },
  { id: 18045, name: "Fountain County, IN" },
  { id: 18047, name: "Franklin County, IN" },
  { id: 18049, name: "Fulton County, IN" },
  { id: 18051, name: "Gibson County, IN" },
  { id: 18053, name: "Grant County, IN" },
  { id: 18055, name: "Greene County, IN" },
  { id: 18057, name: "Hamilton County, IN" },
  { id: 18059, name: "Hancock County, IN" },
  { id: 18061, name: "Harrison County, IN" },
  { id: 18063, name: "Hendricks County, IN" },
  { id: 18065, name: "Henry County, IN" },
  { id: 18067, name: "Howard County, IN" },
  { id: 18069, name: "Huntington County, IN" },
  { id: 18071, name: "Jackson County, IN" },
  { id: 18073, name: "Jasper County, IN" },
  { id: 18075, name: "Jay County, IN" },
  { id: 18077, name: "Jefferson County, IN" },
  { id: 18079, name: "Jennings County, IN" },
  { id: 18081, name: "Johnson County, IN" },
  { id: 18083, name: "Knox County, IN" },
  { id: 18085, name: "Kosciusko County, IN" },
  { id: 18087, name: "LaGrange County, IN" },
  { id: 18089, name: "Lake County, IN" },
  { id: 18091, name: "LaPorte County, IN" },
  { id: 18093, name: "Lawrence County, IN" },
  { id: 18095, name: "Madison County, IN" },
  { id: 18097, name: "Marion County, IN" },
  { id: 18099, name: "Marshall County, IN" },
  { id: 18101, name: "Martin County, IN" },
  { id: 18103, name: "Miami County, IN" },
  { id: 18105, name: "Monroe County, IN" },
  { id: 18107, name: "Montgomery County, IN" },
  { id: 18109, name: "Morgan County, IN" },
  { id: 18111, name: "Newton County, IN" },
  { id: 18113, name: "Noble County, IN" },
  { id: 18115, name: "Ohio County, IN" },
  { id: 18117, name: "Orange County, IN" },
  { id: 18119, name: "Owen County, IN" },
  { id: 18121, name: "Parke County, IN" },
  { id: 18123, name: "Perry County, IN" },
  { id: 18125, name: "Pike County, IN" },
  { id: 18127, name: "Porter County, IN" },
  { id: 18129, name: "Posey County, IN" },
  { id: 18131, name: "Pulaski County, IN" },
  { id: 18133, name: "Putnam County, IN" },
  { id: 18135, name: "Randolph County, IN" },
  { id: 18137, name: "Ripley County, IN" },
  { id: 18139, name: "Rush County, IN" },
  { id: 18141, name: "St. Joseph County, IN" },
  { id: 18143, name: "Scott County, IN" },
  { id: 18145, name: "Shelby County, IN" },
  { id: 18147, name: "Spencer County, IN" },
  { id: 18149, name: "Starke County, IN" },
  { id: 18151, name: "Steuben County, IN" },
  { id: 18153, name: "Sullivan County, IN" },
  { id: 18155, name: "Switzerland County, IN" },
  { id: 18157, name: "Tippecanoe County, IN" },
  { id: 18159, name: "Tipton County, IN" },
  { id: 18161, name: "Union County, IN" },
  { id: 18163, name: "Vanderburgh County, IN" },
  { id: 18165, name: "Vermillion County, IN" },
  { id: 18167, name: "Vigo County, IN" },
  { id: 18169, name: "Wabash County, IN" },
  { id: 18171, name: "Warren County, IN" },
  { id: 18173, name: "Warrick County, IN" },
  { id: 18175, name: "Washington County, IN" },
  { id: 18177, name: "Wayne County, IN" },
  { id: 18179, name: "Wells County, IN" },
  { id: 18181, name: "White County, IN" },
  { id: 18183, name: "Whitley County, IN" },
  { id: 19001, name: "Adair County, IA" },
  { id: 19003, name: "Adams County, IA" },
  { id: 19005, name: "Allamakee County, IA" },
  { id: 19007, name: "Appanoose County, IA" },
  { id: 19009, name: "Audubon County, IA" },
  { id: 19011, name: "Benton County, IA" },
  { id: 19013, name: "Black Hawk County, IA" },
  { id: 19015, name: "Boone County, IA" },
  { id: 19017, name: "Bremer County, IA" },
  { id: 19019, name: "Buchanan County, IA" },
  { id: 19021, name: "Buena Vista County, IA" },
  { id: 19023, name: "Butler County, IA" },
  { id: 19025, name: "Calhoun County, IA" },
  { id: 19027, name: "Carroll County, IA" },
  { id: 19029, name: "Cass County, IA" },
  { id: 19031, name: "Cedar County, IA" },
  { id: 19033, name: "Cerro Gordo County, IA" },
  { id: 19035, name: "Cherokee County, IA" },
  { id: 19037, name: "Chickasaw County, IA" },
  { id: 19039, name: "Clarke County, IA" },
  { id: 19041, name: "Clay County, IA" },
  { id: 19043, name: "Clayton County, IA" },
  { id: 19045, name: "Clinton County, IA" },
  { id: 19047, name: "Crawford County, IA" },
  { id: 19049, name: "Dallas County, IA" },
  { id: 19051, name: "Davis County, IA" },
  { id: 19053, name: "Decatur County, IA" },
  { id: 19055, name: "Delaware County, IA" },
  { id: 19057, name: "Des Moines County, IA" },
  { id: 19059, name: "Dickinson County, IA" },
  { id: 19061, name: "Dubuque County, IA" },
  { id: 19063, name: "Emmet County, IA" },
  { id: 19065, name: "Fayette County, IA" },
  { id: 19067, name: "Floyd County, IA" },
  { id: 19069, name: "Franklin County, IA" },
  { id: 19071, name: "Fremont County, IA" },
  { id: 19073, name: "Greene County, IA" },
  { id: 19075, name: "Grundy County, IA" },
  { id: 19077, name: "Guthrie County, IA" },
  { id: 19079, name: "Hamilton County, IA" },
  { id: 19081, name: "Hancock County, IA" },
  { id: 19083, name: "Hardin County, IA" },
  { id: 19085, name: "Harrison County, IA" },
  { id: 19087, name: "Henry County, IA" },
  { id: 19089, name: "Howard County, IA" },
  { id: 19091, name: "Humboldt County, IA" },
  { id: 19093, name: "Ida County, IA" },
  { id: 19095, name: "Iowa County, IA" },
  { id: 19097, name: "Jackson County, IA" },
  { id: 19099, name: "Jasper County, IA" },
  { id: 19101, name: "Jefferson County, IA" },
  { id: 19103, name: "Johnson County, IA" },
  { id: 19105, name: "Jones County, IA" },
  { id: 19107, name: "Keokuk County, IA" },
  { id: 19109, name: "Kossuth County, IA" },
  { id: 19111, name: "Lee County, IA" },
  { id: 19113, name: "Linn County, IA" },
  { id: 19115, name: "Louisa County, IA" },
  { id: 19117, name: "Lucas County, IA" },
  { id: 19119, name: "Lyon County, IA" },
  { id: 19121, name: "Madison County, IA" },
  { id: 19123, name: "Mahaska County, IA" },
  { id: 19125, name: "Marion County, IA" },
  { id: 19127, name: "Marshall County, IA" },
  { id: 19129, name: "Mills County, IA" },
  { id: 19131, name: "Mitchell County, IA" },
  { id: 19133, name: "Monona County, IA" },
  { id: 19135, name: "Monroe County, IA" },
  { id: 19137, name: "Montgomery County, IA" },
  { id: 19139, name: "Muscatine County, IA" },
  { id: 19141, name: "O'Brien County, IA" },
  { id: 19143, name: "Osceola County, IA" },
  { id: 19145, name: "Page County, IA" },
  { id: 19147, name: "Palo Alto County, IA" },
  { id: 19149, name: "Plymouth County, IA" },
  { id: 19151, name: "Pocahontas County, IA" },
  { id: 19153, name: "Polk County, IA" },
  { id: 19155, name: "Pottawattamie County, IA" },
  { id: 19157, name: "Poweshiek County, IA" },
  { id: 19159, name: "Ringgold County, IA" },
  { id: 19161, name: "Sac County, IA" },
  { id: 19163, name: "Scott County, IA" },
  { id: 19165, name: "Shelby County, IA" },
  { id: 19167, name: "Sioux County, IA" },
  { id: 19169, name: "Story County, IA" },
  { id: 19171, name: "Tama County, IA" },
  { id: 19173, name: "Taylor County, IA" },
  { id: 19175, name: "Union County, IA" },
  { id: 19177, name: "Van Buren County, IA" },
  { id: 19179, name: "Wapello County, IA" },
  { id: 19181, name: "Warren County, IA" },
  { id: 19183, name: "Washington County, IA" },
  { id: 19185, name: "Wayne County, IA" },
  { id: 19187, name: "Webster County, IA" },
  { id: 19189, name: "Winnebago County, IA" },
  { id: 19191, name: "Winneshiek County, IA" },
  { id: 19193, name: "Woodbury County, IA" },
  { id: 19195, name: "Worth County, IA" },
  { id: 19197, name: "Wright County, IA" },
  { id: 20001, name: "Allen County, KS" },
  { id: 20003, name: "Anderson County, KS" },
  { id: 20005, name: "Atchison County, KS" },
  { id: 20007, name: "Barber County, KS" },
  { id: 20009, name: "Barton County, KS" },
  { id: 20011, name: "Bourbon County, KS" },
  { id: 20013, name: "Brown County, KS" },
  { id: 20015, name: "Butler County, KS" },
  { id: 20017, name: "Chase County, KS" },
  { id: 20019, name: "Chautauqua County, KS" },
  { id: 20021, name: "Cherokee County, KS" },
  { id: 20023, name: "Cheyenne County, KS" },
  { id: 20025, name: "Clark County, KS" },
  { id: 20027, name: "Clay County, KS" },
  { id: 20029, name: "Cloud County, KS" },
  { id: 20031, name: "Coffey County, KS" },
  { id: 20033, name: "Comanche County, KS" },
  { id: 20035, name: "Cowley County, KS" },
  { id: 20037, name: "Crawford County, KS" },
  { id: 20039, name: "Decatur County, KS" },
  { id: 20041, name: "Dickinson County, KS" },
  { id: 20043, name: "Doniphan County, KS" },
  { id: 20045, name: "Douglas County, KS" },
  { id: 20047, name: "Edwards County, KS" },
  { id: 20049, name: "Elk County, KS" },
  { id: 20051, name: "Ellis County, KS" },
  { id: 20053, name: "Ellsworth County, KS" },
  { id: 20055, name: "Finney County, KS" },
  { id: 20057, name: "Ford County, KS" },
  { id: 20059, name: "Franklin County, KS" },
  { id: 20061, name: "Geary County, KS" },
  { id: 20063, name: "Gove County, KS" },
  { id: 20065, name: "Graham County, KS" },
  { id: 20067, name: "Grant County, KS" },
  { id: 20069, name: "Gray County, KS" },
  { id: 20071, name: "Greeley County, KS" },
  { id: 20073, name: "Greenwood County, KS" },
  { id: 20075, name: "Hamilton County, KS" },
  { id: 20077, name: "Harper County, KS" },
  { id: 20079, name: "Harvey County, KS" },
  { id: 20081, name: "Haskell County, KS" },
  { id: 20083, name: "Hodgeman County, KS" },
  { id: 20085, name: "Jackson County, KS" },
  { id: 20087, name: "Jefferson County, KS" },
  { id: 20089, name: "Jewell County, KS" },
  { id: 20091, name: "Johnson County, KS" },
  { id: 20093, name: "Kearny County, KS" },
  { id: 20095, name: "Kingman County, KS" },
  { id: 20097, name: "Kiowa County, KS" },
  { id: 20099, name: "Labette County, KS" },
  { id: 20101, name: "Lane County, KS" },
  { id: 20103, name: "Leavenworth County, KS" },
  { id: 20105, name: "Lincoln County, KS" },
  { id: 20107, name: "Linn County, KS" },
  { id: 20109, name: "Logan County, KS" },
  { id: 20111, name: "Lyon County, KS" },
  { id: 20113, name: "McPherson County, KS" },
  { id: 20115, name: "Marion County, KS" },
  { id: 20117, name: "Marshall County, KS" },
  { id: 20119, name: "Meade County, KS" },
  { id: 20121, name: "Miami County, KS" },
  { id: 20123, name: "Mitchell County, KS" },
  { id: 20125, name: "Montgomery County, KS" },
  { id: 20127, name: "Morris County, KS" },
  { id: 20129, name: "Morton County, KS" },
  { id: 20131, name: "Nemaha County, KS" },
  { id: 20133, name: "Neosho County, KS" },
  { id: 20135, name: "Ness County, KS" },
  { id: 20137, name: "Norton County, KS" },
  { id: 20139, name: "Osage County, KS" },
  { id: 20141, name: "Osborne County, KS" },
  { id: 20143, name: "Ottawa County, KS" },
  { id: 20145, name: "Pawnee County, KS" },
  { id: 20147, name: "Phillips County, KS" },
  { id: 20149, name: "Pottawatomie County, KS" },
  { id: 20151, name: "Pratt County, KS" },
  { id: 20151, name: "Pratt County, KS" },
  { id: 20153, name: "Rawlins County, KS" },
  { id: 20155, name: "Reno County, KS" },
  { id: 20157, name: "Republic County, KS" },
  { id: 20159, name: "Rice County, KS" },
  { id: 20161, name: "Riley County, KS" },
  { id: 20163, name: "Rooks County, KS" },
  { id: 20165, name: "Rush County, KS" },
  { id: 20167, name: "Russell County, KS" },
  { id: 20169, name: "Saline County, KS" },
  { id: 20171, name: "Scott County, KS" },
  { id: 20173, name: "Sedgwick County, KS" },
  { id: 20175, name: "Seward County, KS" },
  { id: 20177, name: "Shawnee County, KS" },
  { id: 20179, name: "Sheridan County, KS" },
  { id: 20181, name: "Sherman County, KS" },
  { id: 20183, name: "Smith County, KS" },
  { id: 20185, name: "Stafford County, KS" },
  { id: 20187, name: "Stanton County, KS" },
  { id: 20189, name: "Stevens County, KS" },
  { id: 20191, name: "Sumner County, KS" },
  { id: 20193, name: "Thomas County, KS" },
  { id: 20195, name: "Trego County, KS" },
  { id: 20197, name: "Wabaunsee County, KS" },
  { id: 20199, name: "Wallace County, KS" },
  { id: 20201, name: "Washington County, KS" },
  { id: 20203, name: "Wichita County, KS" },
  { id: 20205, name: "Wilson County, KS" },
  { id: 20207, name: "Woodson County, KS" },
  { id: 20209, name: "Wyandotte County, KS" },
  { id: 21001, name: "Adair County, KY" },
  { id: 21003, name: "Allen County, KY" },
  { id: 21005, name: "Anderson County, KY" },
  { id: 21007, name: "Ballard County, KY" },
  { id: 21009, name: "Barren County, KY" },
  { id: 21011, name: "Bath County, KY" },
  { id: 21013, name: "Bell County, KY" },
  { id: 21015, name: "Boone County, KY" },
  { id: 21017, name: "Bourbon County, KY" },
  { id: 21019, name: "Boyd County, KY" },
  { id: 21021, name: "Boyle County, KY" },
  { id: 21023, name: "Bracken County, KY" },
  { id: 21025, name: "Breathitt County, KY" },
  { id: 21027, name: "Breckinridge County, KY" },
  { id: 21029, name: "Bullitt County, KY" },
  { id: 21031, name: "Butler County, KY" },
  { id: 21033, name: "Caldwell County, KY" },
  { id: 21035, name: "Calloway County, KY" },
  { id: 21037, name: "Campbell County, KY" },
  { id: 21039, name: "Carlisle County, KY" },
  { id: 21041, name: "Carroll County, KY" },
  { id: 21043, name: "Carter County, KY" },
  { id: 21045, name: "Casey County, KY" },
  { id: 21047, name: "Christian County, KY" },
  { id: 21049, name: "Clark County, KY" },
  { id: 21051, name: "Clay County, KY" },
  { id: 21053, name: "Clinton County, KY" },
  { id: 21055, name: "Crittenden County, KY" },
  { id: 21057, name: "Cumberland County, KY" },
  { id: 21059, name: "Daviess County, KY" },
  { id: 21061, name: "Edmonson County, KY" },
  { id: 21063, name: "Elliott County, KY" },
  { id: 21065, name: "Estill County, KY" },
  { id: 21067, name: "Fayette County, KY" },
  { id: 21069, name: "Fleming County, KY" },
  { id: 21071, name: "Floyd County, KY" },
  { id: 21073, name: "Franklin County, KY" },
  { id: 21075, name: "Fulton County, KY" },
  { id: 21077, name: "Gallatin County, KY" },
  { id: 21079, name: "Garrard County, KY" },
  { id: 21081, name: "Grant County, KY" },
  { id: 21083, name: "Graves County, KY" },
  { id: 21085, name: "Grayson County, KY" },
  { id: 21087, name: "Green County, KY" },
  { id: 21089, name: "Greenup County, KY" },
  { id: 21091, name: "Hancock County, KY" },
  { id: 21093, name: "Hardin County, KY" },
  { id: 21095, name: "Harlan County, KY" },
  { id: 21097, name: "Harrison County, KY" },
  { id: 21099, name: "Hart County, KY" },
  { id: 21101, name: "Henderson County, KY" },
  { id: 21103, name: "Henry County, KY" },
  { id: 21105, name: "Hickman County, KY" },
  { id: 21107, name: "Hopkins County, KY" },
  { id: 21109, name: "Jackson County, KY" },
  { id: 21111, name: "Jefferson County, KY" },
  { id: 21113, name: "Jessamine County, KY" },
  { id: 21115, name: "Johnson County, KY" },
  { id: 21117, name: "Kenton County, KY" },
  { id: 21119, name: "Knott County, KY" },
  { id: 21121, name: "Knox County, KY" },
  { id: 21123, name: "Larue County, KY" },
  { id: 21125, name: "Laurel County, KY" },
  { id: 21127, name: "Lawrence County, KY" },
  { id: 21129, name: "Lee County, KY" },
  { id: 21131, name: "Leslie County, KY" },
  { id: 21133, name: "Letcher County, KY" },
  { id: 21135, name: "Lewis County, KY" },
  { id: 21137, name: "Lincoln County, KY" },
  { id: 21139, name: "Livingston County, KY" },
  { id: 21141, name: "Logan County, KY" },
  { id: 21143, name: "Lyon County, KY" },
  { id: 21145, name: "McCracken County, KY" },
  { id: 21147, name: "McCreary County, KY" },
  { id: 21149, name: "McLean County, KY" },
  { id: 21151, name: "Madison County, KY" },
  { id: 21153, name: "Magoffin County, KY" },
  { id: 21155, name: "Marion County, KY" },
  { id: 21157, name: "Marshall County, KY" },
  { id: 21159, name: "Martin County, KY" },
  { id: 21161, name: "Mason County, KY" },
  { id: 21163, name: "Meade County, KY" },
  { id: 21165, name: "Menifee County, KY" },
  { id: 21167, name: "Mercer County, KY" },
  { id: 21169, name: "Metcalfe County, KY" },
  { id: 21171, name: "Monroe County, KY" },
  { id: 21173, name: "Montgomery County, KY" },
  { id: 21175, name: "Morgan County, KY" },
  { id: 21177, name: "Muhlenberg County, KY" },
  { id: 21179, name: "Nelson County, KY" },
  { id: 21181, name: "Nicholas County, KY" },
  { id: 21183, name: "Ohio County, KY" },
  { id: 21185, name: "Oldham County, KY" },
  { id: 21187, name: "Owen County, KY" },
  { id: 21189, name: "Owsley County, KY" },
  { id: 21191, name: "Pendleton County, KY" },
  { id: 21193, name: "Perry County, KY" },
  { id: 21195, name: "Pike County, KY" },
  { id: 21197, name: "Powell County, KY" },
  { id: 21199, name: "Pulaski County, KY" },
  { id: 21201, name: "Robertson County, KY" },
  { id: 21203, name: "Rockcastle County, KY" },
  { id: 21205, name: "Rowan County, KY" },
  { id: 21207, name: "Russell County, KY" },
  { id: 21209, name: "Scott County, KY" },
  { id: 21211, name: "Shelby County, KY" },
  { id: 21213, name: "Simpson County, KY" },
  { id: 21215, name: "Spencer County, KY" },
  { id: 21217, name: "Taylor County, KY" },
  { id: 21219, name: "Todd County, KY" },
  { id: 21221, name: "Trigg County, KY" },
  { id: 21223, name: "Trimble County, KY" },
  { id: 21225, name: "Union County, KY" },
  { id: 21227, name: "Warren County, KY" },
  { id: 21229, name: "Washington County, KY" },
  { id: 21231, name: "Wayne County, KY" },
  { id: 21233, name: "Webster County, KY" },
  { id: 21235, name: "Whitley County, KY" },
  { id: 21237, name: "Wolfe County, KY" },
  { id: 21239, name: "Woodford County, KY" },
  { id: 22001, name: "Acadia Parish, LA" },
  { id: 22003, name: "Allen Parish, LA" },
  { id: 22005, name: "Ascension Parish, LA" },
  { id: 22007, name: "Assumption Parish, LA" },
  { id: 22009, name: "Avoyelles Parish, LA" },
  { id: 22011, name: "Beauregard Parish, LA" },
  { id: 22013, name: "Bienville Parish, LA" },
  { id: 22015, name: "Bossier Parish, LA" },
  { id: 22017, name: "Caddo Parish, LA" },
  { id: 22019, name: "Calcasieu Parish, LA" },
  { id: 22021, name: "Caldwell Parish, LA" },
  { id: 22023, name: "Cameron Parish, LA" },
  { id: 22025, name: "Catahoula Parish, LA" },
  { id: 22027, name: "Claiborne Parish, LA" },
  { id: 22029, name: "Concordia Parish, LA" },
  { id: 22031, name: "De Soto Parish, LA" },
  { id: 22033, name: "East Baton Rouge Parish, LA" },
  { id: 22035, name: "East Carroll Parish, LA" },
  { id: 22037, name: "East Feliciana Parish, LA" },
  { id: 22039, name: "Evangeline Parish, LA" },
  { id: 22041, name: "Franklin Parish, LA" },
  { id: 22043, name: "Grant Parish, LA" },
  { id: 22045, name: "Iberia Parish, LA" },
  { id: 22047, name: "Iberville Parish, LA" },
  { id: 22049, name: "Jackson Parish, LA" },
  { id: 22051, name: "Jefferson Parish, LA" },
  { id: 22053, name: "Jefferson Davis Parish, LA" },
  { id: 22055, name: "Lafayette Parish, LA" },
  { id: 22057, name: "Lafourche Parish, LA" },
  { id: 22059, name: "LaSalle Parish, LA" },
  { id: 22061, name: "Lincoln Parish, LA" },
  { id: 22063, name: "Livingston Parish, LA" },
  { id: 22065, name: "Madison Parish, LA" },
  { id: 22067, name: "Morehouse Parish, LA" },
  { id: 22069, name: "Natchitoches Parish, LA" },
  { id: 22071, name: "Orleans Parish, LA" },
  { id: 22073, name: "Ouachita Parish, LA" },
  { id: 22075, name: "Plaquemines Parish, LA" },
  { id: 22077, name: "Pointe Coupee Parish, LA" },
  { id: 22079, name: "Rapides Parish, LA" },
  { id: 22081, name: "Red River Parish, LA" },
  { id: 22083, name: "Richland Parish, LA" },
  { id: 22085, name: "Sabine Parish, LA" },
  { id: 22087, name: "St. Bernard Parish, LA" },
  { id: 22089, name: "St. Charles Parish, LA" },
  { id: 22091, name: "St. Helena Parish, LA" },
  { id: 22093, name: "St. James Parish, LA" },
  { id: 22095, name: "St. John the Baptist Parish, LA" },
  { id: 22097, name: "St. Landry Parish, LA" },
  { id: 22099, name: "St. Martin Parish, LA" },
  { id: 22101, name: "St. Mary Parish, LA" },
  { id: 22103, name: "St. Tammany Parish, LA" },
  { id: 22105, name: "Tangipahoa Parish, LA" },
  { id: 22107, name: "Tensas Parish, LA" },
  { id: 22109, name: "Terrebonne Parish, LA" },
  { id: 22111, name: "Union Parish, LA" },
  { id: 22113, name: "Vermilion Parish, LA" },
  { id: 22115, name: "Vernon Parish, LA" },
  { id: 22117, name: "Washington Parish, LA" },
  { id: 22119, name: "Webster Parish, LA" },
  { id: 22121, name: "West Baton Rouge Parish, LA" },
  { id: 22123, name: "West Carroll Parish, LA" },
  { id: 22125, name: "West Feliciana Parish, LA" },
  { id: 22127, name: "Winn Parish, LA" },
  { id: 23001, name: "Androscoggin County, ME" },
  { id: 23003, name: "Aroostook County, ME" },
  { id: 23005, name: "Cumberland County, ME" },
  { id: 23007, name: "Franklin County, ME" },
  { id: 23009, name: "Hancock County, ME" },
  { id: 23011, name: "Kennebec County, ME" },
  { id: 23013, name: "Knox County, ME" },
  { id: 23015, name: "Lincoln County, ME" },
  { id: 23017, name: "Oxford County, ME" },
  { id: 23019, name: "Penobscot County, ME" },
  { id: 23021, name: "Piscataquis County, ME" },
  { id: 23023, name: "Sagadahoc County, ME" },
  { id: 23025, name: "Somerset County, ME" },
  { id: 23027, name: "Waldo County, ME" },
  { id: 23029, name: "Washington County, ME" },
  { id: 23031, name: "York County, ME" },
  { id: 24001, name: "Allegany County, MD" },
  { id: 24003, name: "Anne Arundel County, MD" },
  { id: 24005, name: "Baltimore County, MD" },
  { id: 24009, name: "Calvert County, MD" },
  { id: 24011, name: "Caroline County, MD" },
  { id: 24013, name: "Carroll County, MD" },
  { id: 24015, name: "Cecil County, MD" },
  { id: 24017, name: "Charles County, MD" },
  { id: 24019, name: "Dorchester County, MD" },
  { id: 24021, name: "Frederick County, MD" },
  { id: 24023, name: "Garrett County, MD" },
  { id: 24025, name: "Harford County, MD" },
  { id: 24027, name: "Howard County, MD" },
  { id: 24029, name: "Kent County, MD" },
  { id: 24031, name: "Montgomery County, MD" },
  { id: 24033, name: "Prince George's County, MD" },
  { id: 24035, name: "Queen Anne's County, MD" },
  { id: 24037, name: "St. Mary's County, MD" },
  { id: 24039, name: "Somerset County, MD" },
  { id: 24041, name: "Talbot County, MD" },
  { id: 24043, name: "Washington County, MD" },
  { id: 24045, name: "Wicomico County, MD" },
  { id: 24047, name: "Worcester County, MD" },
  { id: 24510, name: "Baltimore city, MD" },
  { id: 25001, name: "Barnstable County, MA" },
  { id: 25003, name: "Berkshire County, MA" },
  { id: 25005, name: "Bristol County, MA" },
  { id: 25007, name: "Dukes County, MA" },
  { id: 25009, name: "Essex County, MA" },
  { id: 25011, name: "Franklin County, MA" },
  { id: 25013, name: "Hampden County, MA" },
  { id: 25015, name: "Hampshire County, MA" },
  { id: 25017, name: "Middlesex County, MA" },
  { id: 25019, name: "Nantucket County/town, MA" },
  { id: 25021, name: "Norfolk County, MA" },
  { id: 25023, name: "Plymouth County, MA" },
  { id: 25025, name: "Suffolk County, MA" },
  { id: 25027, name: "Worcester County, MA" },
  { id: 26001, name: "Alcona County, MI" },
    { id: 26003, name: "Alger County, MI" },
    { id: 26005, name: "Allegan County, MI" },
    { id: 26007, name: "Alpena County, MI" },
    { id: 26009, name: "Antrim County, MI" },
    { id: 26011, name: "Arenac County, MI" },
    { id: 26013, name: "Baraga County, MI" },
    { id: 26015, name: "Barry County, MI" },
    { id: 26017, name: "Bay County, MI" },
    { id: 26019, name: "Benzie County, MI" },
    { id: 26021, name: "Berrien County, MI" },
    { id: 26023, name: "Branch County, MI" },
    { id: 26025, name: "Calhoun County, MI" },
    { id: 26027, name: "Cass County, MI" },
    { id: 26029, name: "Charlevoix County, MI" },
    { id: 26031, name: "Cheboygan County, MI" },
    { id: 26033, name: "Chippewa County, MI" },
    { id: 26035, name: "Clare County, MI" },
    { id: 26037, name: "Clinton County, MI" },
    { id: 26039, name: "Crawford County, MI" },
    { id: 26041, name: "Delta County, MI" },
    { id: 26043, name: "Dickinson County, MI" },
    { id: 26045, name: "Eaton County, MI" },
    { id: 26047, name: "Emmet County, MI" },
    { id: 26049, name: "Genesee County, MI" },
    { id: 26051, name: "Gladwin County, MI" },
    { id: 26053, name: "Gogebic County, MI" },
    { id: 26055, name: "Grand Traverse County, MI" },
    { id: 26057, name: "Gratiot County, MI" },
    { id: 26059, name: "Hillsdale County, MI" },
    { id: 26061, name: "Houghton County, MI" },
    { id: 26063, name: "Huron County, MI" },
    { id: 26065, name: "Ingham County, MI" },
    { id: 26067, name: "Ionia County, MI" },
    { id: 26069, name: "Iosco County, MI" },
    { id: 26071, name: "Iron County, MI" },
    { id: 26073, name: "Isabella County, MI" },
    { id: 26075, name: "Jackson County, MI" },
    { id: 26077, name: "Kalamazoo County, MI" },
    { id: 26079, name: "Kalkaska County, MI" },
    { id: 26081, name: "Kent County, MI" },
    { id: 26083, name: "Keweenaw County, MI" },
    { id: 26085, name: "Lake County, MI" },
    { id: 26087, name: "Lapeer County, MI" },
    { id: 26089, name: "Leelanau County, MI" },
    { id: 26091, name: "Lenawee County, MI" },
    { id: 26093, name: "Livingston County, MI" },
    { id: 26095, name: "Luce County, MI" },
    { id: 26097, name: "Mackinac County, MI" },
    { id: 26099, name: "Macomb County, MI" },
    { id: 26101, name: "Manistee County, MI" },
    { id: 26103, name: "Marquette County, MI" },
    { id: 26105, name: "Mason County, MI" },
    { id: 26107, name: "Mecosta County, MI" },
    { id: 26109, name: "Menominee County, MI" },
    { id: 26111, name: "Midland County, MI" },
    { id: 26113, name: "Missaukee County, MI" },
    { id: 26115, name: "Monroe County, MI" },
    { id: 26117, name: "Montcalm County, MI" },
    { id: 26119, name: "Montmorency County, MI" },
    { id: 26121, name: "Muskegon County, MI" },
    { id: 26123, name: "Newaygo County, MI" },
    { id: 26125, name: "Oakland County, MI" },
    { id: 26127, name: "Oceana County, MI" },
    { id: 26129, name: "Ogemaw County, MI" },
    { id: 26131, name: "Ontonagon County, MI" },
    { id: 26133, name: "Osceola County, MI" },
    { id: 26135, name: "Oscoda County, MI" },
    { id: 26137, name: "Otsego County, MI" },
    { id: 26139, name: "Ottawa County, MI" },
    { id: 26141, name: "Presque Isle County, MI" },
    { id: 26143, name: "Roscommon County, MI" },
    { id: 26145, name: "Saginaw County, MI" },
    { id: 26147, name: "St. Clair County, MI" },
    { id: 26149, name: "St. Joseph County, MI" },
    { id: 26151, name: "Sanilac County, MI" },
    { id: 26153, name: "Schoolcraft County, MI" },
    { id: 26155, name: "Shiawassee County, MI" },
    { id: 26157, name: "Tuscola County, MI" },
    { id: 26159, name: "Van Buren County, MI" },
    { id: 26161, name: "Washtenaw County, MI" },
    { id: 26163, name: "Wayne County, MI" },
    { id: 26165, name: "Wexford County, MI" },
    { id: 27001, name: "Aitkin County, MN" },
    { id: 27003, name: "Anoka County, MN" },
    { id: 27005, name: "Becker County, MN" },
    { id: 27007, name: "Beltrami County, MN" },
    { id: 27009, name: "Benton County, MN" },
    { id: 27011, name: "Big Stone County, MN" },
    { id: 27013, name: "Blue Earth County, MN" },
    { id: 27015, name: "Brown County, MN" },
    { id: 27017, name: "Carlton County, MN" },
    { id: 27019, name: "Carver County, MN" },
    { id: 27021, name: "Cass County, MN" },
    { id: 27023, name: "Chippewa County, MN" },
    { id: 27025, name: "Chisago County, MN" },
    { id: 27027, name: "Clay County, MN" },
    { id: 27029, name: "Clearwater County, MN" },
    { id: 27031, name: "Cook County, MN" },
    { id: 27033, name: "Cottonwood County, MN" },
    { id: 27035, name: "Crow Wing County, MN" },
    { id: 27037, name: "Dakota County, MN" },
    { id: 27039, name: "Dodge County, MN" },
    { id: 27041, name: "Douglas County, MN" },
    { id: 27043, name: "Faribault County, MN" },
    { id: 27045, name: "Fillmore County, MN" },
    { id: 27047, name: "Freeborn County, MN" },
    { id: 27049, name: "Goodhue County, MN" },
    { id: 27051, name: "Grant County, MN" },
    { id: 27053, name: "Hennepin County, MN" },
    { id: 27055, name: "Houston County, MN" },
    { id: 27057, name: "Hubbard County, MN" },
    { id: 27059, name: "Isanti County, MN" },
    { id: 27061, name: "Itasca County, MN" },
    { id: 27063, name: "Jackson County, MN" },
    { id: 27065, name: "Kanabec County, MN" },
    { id: 27067, name: "Kandiyohi County, MN" },
    { id: 27069, name: "Kittson County, MN" },
    { id: 27071, name: "Koochiching County, MN" },
    { id: 27073, name: "Lac qui Parle County, MN" },
    { id: 27075, name: "Lake County, MN" },
    { id: 27077, name: "Lake of the Woods County, MN" },
    { id: 27079, name: "Le Sueur County, MN" },
    { id: 27081, name: "Lincoln County, MN" },
    { id: 27083, name: "Lyon County, MN" },
    { id: 27085, name: "McLeod County, MN" },
    { id: 27087, name: "Mahnomen County, MN" },
    { id: 27089, name: "Marshall County, MN" },
    { id: 27091, name: "Martin County, MN" },
    { id: 27093, name: "Meeker County, MN" },
    { id: 27095, name: "Mille Lacs County, MN" },
    { id: 27097, name: "Morrison County, MN" },
    { id: 27099, name: "Mower County, MN" },
    { id: 27101, name: "Murray County, MN" },
    { id: 27103, name: "Nicollet County, MN" },
    { id: 27105, name: "Nobles County, MN" },
    { id: 27107, name: "Norman County, MN" },
    { id: 27109, name: "Olmsted County, MN" },
    { id: 27111, name: "Otter Tail County, MN" },
    { id: 27113, name: "Pennington County, MN" },
    { id: 27115, name: "Pine County, MN" },
    { id: 27117, name: "Pipestone County, MN" },
    { id: 27119, name: "Polk County, MN" },
    { id: 27121, name: "Pope County, MN" },
    { id: 27123, name: "Ramsey County, MN" },
    { id: 27125, name: "Red Lake County, MN" },
    { id: 27127, name: "Redwood County, MN" },
    { id: 27129, name: "Renville County, MN" },
    { id: 27131, name: "Rice County, MN" },
    { id: 27133, name: "Rock County, MN" },
    { id: 27135, name: "Roseau County, MN" },
    { id: 27137, name: "St. Louis County, MN" },
    { id: 27139, name: "Scott County, MN" },
    { id: 27141, name: "Sherburne County, MN" },
    { id: 27143, name: "Sibley County, MN" },
    { id: 27145, name: "Stearns County, MN" },
    { id: 27147, name: "Steele County, MN" },
    { id: 27149, name: "Stevens County, MN" },
    { id: 27151, name: "Swift County, MN" },
    { id: 27153, name: "Todd County, MN" },
    { id: 27155, name: "Traverse County, MN" },
    { id: 27157, name: "Wabasha County, MN" },
    { id: 27159, name: "Wadena County, MN" },
    { id: 27161, name: "Waseca County, MN" },
    { id: 27163, name: "Washington County, MN" },
    { id: 27165, name: "Watonwan County, MN" },
    { id: 27167, name: "Wilkin County, MN" },
    { id: 27169, name: "Winona County, MN" },
    { id: 27171, name: "Wright County, MN" },
    { id: 27173, name: "Yellow Medicine County, MN" },
    { id: 28001, name: "Adams County, MS" },
  { id: 28003, name: "Alcorn County, MS" },
  { id: 28005, name: "Amite County, MS" },
  { id: 28007, name: "Attala County, MS" },
  { id: 28009, name: "Benton County, MS" },
  { id: 28011, name: "Bolivar County, MS" },
  { id: 28013, name: "Calhoun County, MS" },
  { id: 28015, name: "Carroll County, MS" },
  { id: 28017, name: "Chickasaw County, MS" },
  { id: 28019, name: "Choctaw County, MS" },
  { id: 28021, name: "Claiborne County, MS" },
  { id: 28023, name: "Clarke County, MS" },
  { id: 28025, name: "Clay County, MS" },
  { id: 28027, name: "Coahoma County, MS" },
  { id: 28029, name: "Copiah County, MS" },
  { id: 28031, name: "Covington County, MS" },
  { id: 28033, name: "DeSoto County, MS" },
  { id: 28035, name: "Forrest County, MS" },
  { id: 28037, name: "Franklin County, MS" },
  { id: 28039, name: "George County, MS" },
  { id: 28041, name: "Greene County, MS" },
  { id: 28043, name: "Grenada County, MS" },
  { id: 28045, name: "Hancock County, MS" },
  { id: 28047, name: "Harrison County, MS" },
  { id: 28049, name: "Hinds County, MS" },
  { id: 28051, name: "Holmes County, MS" },
  { id: 28053, name: "Humphreys County, MS" },
  { id: 28055, name: "Issaquena County, MS" },
  { id: 28057, name: "Itawamba County, MS" },
  { id: 28059, name: "Jackson County, MS" },
  { id: 28061, name: "Jasper County, MS" },
  { id: 28063, name: "Jefferson County, MS" },
  { id: 28065, name: "Jefferson Davis County, MS" },
  { id: 28067, name: "Jones County, MS" },
  { id: 28069, name: "Kemper County, MS" },
  { id: 28071, name: "Lafayette County, MS" },
  { id: 28073, name: "Lamar County, MS" },
  { id: 28075, name: "Lauderdale County, MS" },
  { id: 28077, name: "Lawrence County, MS" },
  { id: 28079, name: "Leake County, MS" },
  { id: 28081, name: "Lee County, MS" },
  { id: 28083, name: "Leflore County, MS" },
  { id: 28085, name: "Lincoln County, MS" },
  { id: 28087, name: "Lowndes County, MS" },
  { id: 28089, name: "Madison County, MS" },
  { id: 28091, name: "Marion County, MS" },
  { id: 28093, name: "Marshall County, MS" },
  { id: 28095, name: "Monroe County, MS" },
  { id: 28097, name: "Montgomery County, MS" },
  { id: 28099, name: "Neshoba County, MS" },
  { id: 28101, name: "Newton County, MS" },
  { id: 28103, name: "Noxubee County, MS" },
  { id: 28105, name: "Oktibbeha County, MS" },
  { id: 28107, name: "Panola County, MS" },
  { id: 28109, name: "Pearl River County, MS" },
  { id: 28111, name: "Perry County, MS" },
  { id: 28113, name: "Pike County, MS" },
  { id: 28115, name: "Pontotoc County, MS" },
  { id: 28117, name: "Prentiss County, MS" },
  { id: 28119, name: "Quitman County, MS" },
  { id: 28121, name: "Rankin County, MS" },
  { id: 28123, name: "Scott County, MS" },
  { id: 28125, name: "Sharkey County, MS" },
  { id: 28127, name: "Simpson County, MS" },
  { id: 28129, name: "Smith County, MS" },
  { id: 28131, name: "Stone County, MS" },
  { id: 28133, name: "Sunflower County, MS" },
  { id: 28135, name: "Tallahatchie County, MS" },
  { id: 28137, name: "Tate County, MS" },
  { id: 28139, name: "Tippah County, MS" },
  { id: 28141, name: "Tishomingo County, MS" },
  { id: 28143, name: "Tunica County, MS" },
  { id: 28145, name: "Union County, MS" },
  { id: 28147, name: "Walthall County, MS" },
  { id: 28149, name: "Warren County, MS" },
  { id: 28151, name: "Washington County, MS" },
  { id: 28153, name: "Wayne County, MS" },
  { id: 28155, name: "Webster County, MS" },
  { id: 28157, name: "Wilkinson County, MS" },
  { id: 28159, name: "Winston County, MS" },
  { id: 28161, name: "Yalobusha County, MS" },
  { id: 28163, name: "Yazoo County, MS" },
  { id: 29001, name: "Adair County, MO" },
  { id: 29003, name: "Andrew County, MO" },
  { id: 29005, name: "Atchison County, MO" },
  { id: 29007, name: "Audrain County, MO" },
  { id: 29009, name: "Barry County, MO" },
  { id: 29011, name: "Barton County, MO" },
  { id: 29013, name: "Bates County, MO" },
  { id: 29015, name: "Benton County, MO" },
  { id: 29017, name: "Bollinger County, MO" },
  { id: 29019, name: "Boone County, MO" },
  { id: 29021, name: "Buchanan County, MO" },
  { id: 29023, name: "Butler County, MO" },
  { id: 29025, name: "Caldwell County, MO" },
  { id: 29027, name: "Callaway County, MO" },
  { id: 29029, name: "Camden County, MO" },
  { id: 29031, name: "Cape Girardeau County, MO" },
  { id: 29033, name: "Carroll County, MO" },
  { id: 29035, name: "Carter County, MO" },
  { id: 29037, name: "Cass County, MO" },
  { id: 29039, name: "Cedar County, MO" },
  { id: 29041, name: "Chariton County, MO" },
  { id: 29043, name: "Christian County, MO" },
  { id: 29045, name: "Clark County, MO" },
  { id: 29047, name: "Clay County, MO" },
  { id: 29049, name: "Clinton County, MO" },
  { id: 29051, name: "Cole County, MO" },
  { id: 29053, name: "Cooper County, MO" },
  { id: 29055, name: "Crawford County, MO" },
  { id: 29057, name: "Dade County, MO" },
  { id: 29059, name: "Dallas County, MO" },
  { id: 29061, name: "Daviess County, MO" },
  { id: 29063, name: "DeKalb County, MO" },
  { id: 29065, name: "Dent County, MO" },
  { id: 29067, name: "Douglas County, MO" },
  { id: 29069, name: "Dunklin County, MO" },
  { id: 29071, name: "Franklin County, MO" },
  { id: 29073, name: "Gasconade County, MO" },
  { id: 29075, name: "Gentry County, MO" },
  { id: 29077, name: "Greene County, MO" },
  { id: 29079, name: "Grundy County, MO" },
  { id: 29081, name: "Harrison County, MO" },
  { id: 29083, name: "Henry County, MO" },
  { id: 29085, name: "Hickory County, MO" },
  { id: 29087, name: "Holt County, MO" },
  { id: 29089, name: "Howard County, MO" },
  { id: 29091, name: "Howell County, MO" },
  { id: 29093, name: "Iron County, MO" },
  { id: 29095, name: "Jackson County, MO" },
  { id: 29097, name: "Jasper County, MO" },
  { id: 29099, name: "Jefferson County, MO" },
  { id: 29101, name: "Johnson County, MO" },
  { id: 29103, name: "Knox County, MO" },
  { id: 29105, name: "Laclede County, MO" },
  { id: 29107, name: "Lafayette County, MO" },
  { id: 29109, name: "Lawrence County, MO" },
  { id: 29111, name: "Lewis County, MO" },
  { id: 29113, name: "Lincoln County, MO" },
  { id: 29115, name: "Linn County, MO" },
  { id: 29117, name: "Livingston County, MO" },
  { id: 29119, name: "McDonald County, MO" },
  { id: 29121, name: "Macon County, MO" },
  { id: 29123, name: "Madison County, MO" },
  { id: 29125, name: "Maries County, MO" },
  { id: 29127, name: "Marion County, MO" },
  { id: 29129, name: "Mercer County, MO" },
  { id: 29131, name: "Miller County, MO" },
  { id: 29133, name: "Mississippi County, MO" },
  { id: 29135, name: "Moniteau County, MO" },
  { id: 29137, name: "Monroe County, MO" },
  { id: 29139, name: "Montgomery County, MO" },
  { id: 29141, name: "Morgan County, MO" },
  { id: 29143, name: "New Madrid County, MO" },
  { id: 29145, name: "Newton County, MO" },
  { id: 29147, name: "Nodaway County, MO" },
  { id: 29149, name: "Oregon County, MO" },
  { id: 29151, name: "Osage County, MO" },
  { id: 29153, name: "Ozark County, MO" },
  { id: 29155, name: "Pemiscot County, MO" },
  { id: 29157, name: "Perry County, MO" },
  { id: 29159, name: "Pettis County, MO" },
  { id: 29161, name: "Phelps County, MO" },
  { id: 29163, name: "Pike County, MO" },
  { id: 29165, name: "Platte County, MO" },
  { id: 29167, name: "Polk County, MO" },
  { id: 29169, name: "Pulaski County, MO" },
  { id: 29171, name: "Putnam County, MO" },
  { id: 29173, name: "Ralls County, MO" },
  { id: 29175, name: "Randolph County, MO" },
  { id: 29177, name: "Ray County, MO" },
  { id: 29179, name: "Reynolds County, MO" },
  { id: 29181, name: "Ripley County, MO" },
  { id: 29183, name: "St. Charles County, MO" },
  { id: 29185, name: "St. Clair County, MO" },
  { id: 29186, name: "Ste. Genevieve County, MO" },
  { id: 29187, name: "St. Francois County, MO" },
  { id: 29189, name: "St. Louis County, MO" },
  { id: 29195, name: "Saline County, MO" },
  { id: 29197, name: "Schuyler County, MO" },
  { id: 29199, name: "Scotland County, MO" },
  { id: 29201, name: "Scott County, MO" },
  { id: 29203, name: "Shannon County, MO" },
  { id: 29205, name: "Shelby County, MO" },
  { id: 29207, name: "Stoddard County, MO" },
  { id: 29209, name: "Stone County, MO" },
  { id: 29211, name: "Sullivan County, MO" },
  { id: 29213, name: "Taney County, MO" },
  { id: 29215, name: "Texas County, MO" },
  { id: 29217, name: "Vernon County, MO" },
  { id: 29219, name: "Warren County, MO" },
  { id: 29221, name: "Washington County, MO" },
  { id: 29223, name: "Wayne County, MO" },
  { id: 29225, name: "Webster County, MO" },
  { id: 29227, name: "Worth County, MO" },
  { id: 29229, name: "Wright County, MO" },
  { id: 29510, name: "St. Louis city, MO" },
  { id: 30001, name: "Beaverhead County, MT" },
  { id: 30003, name: "Big Horn County, MT" },
  { id: 30005, name: "Blaine County, MT" },
  { id: 30007, name: "Broadwater County, MT" },
  { id: 30009, name: "Carbon County, MT" },
  { id: 30011, name: "Carter County, MT" },
  { id: 30013, name: "Cascade County, MT" },
  { id: 30015, name: "Chouteau County, MT" },
  { id: 30017, name: "Custer County, MT" },
  { id: 30019, name: "Daniels County, MT" },
  { id: 30021, name: "Dawson County, MT" },
  { id: 30023, name: "Deer Lodge County, MT" },
  { id: 30025, name: "Fallon County, MT" },
  { id: 30027, name: "Fergus County, MT" },
  { id: 30029, name: "Flathead County, MT" },
  { id: 30031, name: "Gallatin County, MT" },
  { id: 30033, name: "Garfield County, MT" },
  { id: 30035, name: "Glacier County, MT" },
  { id: 30037, name: "Golden Valley County, MT" },
  { id: 30039, name: "Granite County, MT" },
  { id: 30041, name: "Hill County, MT" },
  { id: 30043, name: "Jefferson County, MT" },
  { id: 30045, name: "Judith Basin County, MT" },
  { id: 30047, name: "Lake County, MT" },
  { id: 30049, name: "Lewis and Clark County, MT" },
  { id: 30051, name: "Liberty County, MT" },
  { id: 30053, name: "Lincoln County, MT" },
  { id: 30055, name: "McCone County, MT" },
  { id: 30057, name: "Madison County, MT" },
  { id: 30059, name: "Meagher County, MT" },
  { id: 30061, name: "Mineral County, MT" },
  { id: 30063, name: "Missoula County, MT" },
  { id: 30065, name: "Musselshell County, MT" },
  { id: 30067, name: "Park County, MT" },
  { id: 30069, name: "Petroleum County, MT" },
  { id: 30071, name: "Phillips County, MT" },
  { id: 30073, name: "Pondera County, MT" },
  { id: 30075, name: "Powder River County, MT" },
  { id: 30077, name: "Powell County, MT" },
  { id: 30079, name: "Prairie County, MT" },
  { id: 30081, name: "Ravalli County, MT" },
  { id: 30083, name: "Richland County, MT" },
  { id: 30085, name: "Roosevelt County, MT" },
  { id: 30087, name: "Rosebud County, MT" },
  { id: 30089, name: "Sanders County, MT" },
  { id: 30091, name: "Sheridan County, MT" },
  { id: 30093, name: "Silver Bow County, MT" },
  { id: 30095, name: "Stillwater County, MT" },
  { id: 30097, name: "Sweet Grass County, MT" },
  { id: 30099, name: "Teton County, MT" },
  { id: 30101, name: "Toole County, MT" },
  { id: 30103, name: "Treasure County, MT" },
  { id: 30105, name: "Valley County, MT" },
  { id: 30107, name: "Wheatland County, MT" },
  { id: 30109, name: "Wibaux County, MT" },
  { id: 30111, name: "Yellowstone County, MT" },
  { id: 31001, name: "Adams County, NE" },
  { id: 31003, name: "Antelope County, NE" },
  { id: 31005, name: "Arthur County, NE" },
  { id: 31007, name: "Banner County, NE" },
  { id: 31009, name: "Blaine County, NE" },
  { id: 31011, name: "Boone County, NE" },
  { id: 31013, name: "Box Butte County, NE" },
  { id: 31015, name: "Boyd County, NE" },
  { id: 31017, name: "Brown County, NE" },
  { id: 31019, name: "Buffalo County, NE" },
  { id: 31021, name: "Burt County, NE" },
  { id: 31023, name: "Butler County, NE" },
  { id: 31025, name: "Cass County, NE" },
  { id: 31027, name: "Cedar County, NE" },
  { id: 31029, name: "Chase County, NE" },
  { id: 31031, name: "Cherry County, NE" },
  { id: 31033, name: "Cheyenne County, NE" },
  { id: 31035, name: "Clay County, NE" },
  { id: 31037, name: "Colfax County, NE" },
  { id: 31039, name: "Cuming County, NE" },
  { id: 31041, name: "Custer County, NE" },
  { id: 31043, name: "Dakota County, NE" },
  { id: 31045, name: "Dawes County, NE" },
  { id: 31047, name: "Dawson County, NE" },
  { id: 31049, name: "Deuel County, NE" },
  { id: 31051, name: "Dixon County, NE" },
  { id: 31053, name: "Dodge County, NE" },
  { id: 31055, name: "Douglas County, NE" },
  { id: 31057, name: "Dundy County, NE" },
  { id: 31059, name: "Fillmore County, NE" },
  { id: 31061, name: "Franklin County, NE" },
  { id: 31063, name: "Frontier County, NE" },
  { id: 31065, name: "Furnas County, NE" },
  { id: 31067, name: "Gage County, NE" },
  { id: 31069, name: "Garden County, NE" },
  { id: 31071, name: "Garfield County, NE" },
  { id: 31073, name: "Gosper County, NE" },
  { id: 31075, name: "Grant County, NE" },
  { id: 31077, name: "Greeley County, NE" },
  { id: 31079, name: "Hall County, NE" },
  { id: 31081, name: "Hamilton County, NE" },
  { id: 31083, name: "Harlan County, NE" },
  { id: 31085, name: "Hayes County, NE" },
  { id: 31087, name: "Hitchcock County, NE" },
  { id: 31089, name: "Holt County, NE" },
  { id: 31091, name: "Hooker County, NE" },
  { id: 31093, name: "Howard County, NE" },
  { id: 31095, name: "Jefferson County, NE" },
  { id: 31097, name: "Johnson County, NE" },
  { id: 31099, name: "Kearney County, NE" },
  { id: 31101, name: "Keith County, NE" },
  { id: 31103, name: "Keya Paha County, NE" },
  { id: 31105, name: "Kimball County, NE" },
  { id: 31107, name: "Knox County, NE" },
  { id: 31109, name: "Lancaster County, NE" },
  { id: 31111, name: "Lincoln County, NE" },
  { id: 31113, name: "Logan County, NE" },
  { id: 31115, name: "Loup County, NE" },
  { id: 31117, name: "McPherson County, NE" },
  { id: 31119, name: "Madison County, NE" },
  { id: 31121, name: "Merrick County, NE" },
  { id: 31123, name: "Morrill County, NE" },
  { id: 31125, name: "Nance County, NE" },
  { id: 31127, name: "Nemaha County, NE" },
  { id: 31129, name: "Nuckolls County, NE" },
  { id: 31131, name: "Otoe County, NE" },
  { id: 31133, name: "Pawnee County, NE" },
  { id: 31135, name: "Perkins County, NE" },
  { id: 31137, name: "Phelps County, NE" },
  { id: 31139, name: "Pierce County, NE" },
  { id: 31141, name: "Platte County, NE" },
  { id: 31143, name: "Polk County, NE" },
  { id: 31145, name: "Red Willow County, NE" },
  { id: 31147, name: "Richardson County, NE" },
  { id: 31149, name: "Rock County, NE" },
  { id: 31151, name: "Saline County, NE" },
  { id: 31153, name: "Sarpy County, NE" },
  { id: 31155, name: "Saunders County, NE" },
  { id: 31157, name: "Scotts Bluff County, NE" },
  { id: 31159, name: "Seward County, NE" },
  { id: 31161, name: "Sheridan County, NE" },
  { id: 31163, name: "Sherman County, NE" },
  { id: 31165, name: "Sioux County, NE" },
  { id: 31167, name: "Stanton County, NE" },
  { id: 31169, name: "Thayer County, NE" },
  { id: 31171, name: "Thomas County, NE" },
  { id: 31173, name: "Thurston County, NE" },
  { id: 31175, name: "Valley County, NE" },
  { id: 31177, name: "Washington County, NE" },
  { id: 31179, name: "Wayne County, NE" },
  { id: 31181, name: "Webster County, NE" },
  { id: 31183, name: "Wheeler County, NE" },
  { id: 31185, name: "York County, NE" },
  { id: 32001, name: "Churchill County, NV" },
  { id: 32003, name: "Clark County, NV" },
  { id: 32005, name: "Douglas County, NV" },
  { id: 32007, name: "Elko County, NV" },
  { id: 32009, name: "Esmeralda County, NV" },
  { id: 32011, name: "Eureka County, NV" },
  { id: 32013, name: "Humboldt County, NV" },
  { id: 32015, name: "Lander County, NV" },
  { id: 32017, name: "Lincoln County, NV" },
  { id: 32019, name: "Lyon County, NV" },
  { id: 32021, name: "Mineral County, NV" },
  { id: 32023, name: "Nye County, NV" },
  { id: 32027, name: "Pershing County, NV" },
  { id: 32029, name: "Storey County, NV" },
  { id: 32031, name: "Washoe County, NV" },
  { id: 32033, name: "White Pine County, NV" },
  { id: 32510, name: "Carson City, NV" },
  { id: 33001, name: "Belknap County, NH" },
  { id: 33003, name: "Carroll County, NH" },
  { id: 33005, name: "Cheshire County, NH" },
  { id: 33007, name: "Coos County, NH" },
  { id: 33009, name: "Grafton County, NH" },
  { id: 33011, name: "Hillsborough County, NH" },
  { id: 33013, name: "Merrimack County, NH" },
  { id: 33015, name: "Rockingham County, NH" },
  { id: 33017, name: "Strafford County, NH" },
  { id: 33019, name: "Sullivan County, NH" },
  { id: 34001, name: "Atlantic County, NJ" },
  { id: 34003, name: "Bergen County, NJ" },
  { id: 34005, name: "Burlington County, NJ" },
  { id: 34007, name: "Camden County, NJ" },
  { id: 34009, name: "Cape May County, NJ" },
  { id: 34011, name: "Cumberland County, NJ" },
  { id: 34013, name: "Essex County, NJ" },
  { id: 34015, name: "Gloucester County, NJ" },
  { id: 34017, name: "Hudson County, NJ" },
  { id: 34019, name: "Hunterdon County, NJ" },
  { id: 34021, name: "Mercer County, NJ" },
  { id: 34023, name: "Middlesex County, NJ" },
  { id: 34025, name: "Monmouth County, NJ" },
  { id: 34027, name: "Morris County, NJ" },
  { id: 34029, name: "Ocean County, NJ" },
  { id: 34031, name: "Passaic County, NJ" },
  { id: 34033, name: "Salem County, NJ" },
  { id: 34035, name: "Somerset County, NJ" },
  { id: 34037, name: "Sussex County, NJ" },
  { id: 34039, name: "Union County, NJ" },
  { id: 34041, name: "Warren County, NJ" },
  { id: 35001, name: "Bernalillo County, NM" },
  { id: 35003, name: "Catron County, NM" },
  { id: 35005, name: "Chaves County, NM" },
  { id: 35006, name: "Cibola County, NM" },
  { id: 35007, name: "Colfax County, NM" },
  { id: 35009, name: "Curry County, NM" },
  { id: 35011, name: "De Baca County, NM" },
  { id: 35013, name: "Dona Ana County, NM" },
  { id: 35015, name: "Eddy County, NM" },
  { id: 35017, name: "Grant County, NM" },
  { id: 35019, name: "Guadalupe County, NM" },
  { id: 35021, name: "Harding County, NM" },
  { id: 35023, name: "Hidalgo County, NM" },
  { id: 35025, name: "Lea County, NM" },
  { id: 35027, name: "Lincoln County, NM" },
  { id: 35028, name: "Los Alamos County, NM" },
  { id: 35029, name: "Luna County, NM" },
  { id: 35031, name: "McKinley County, NM" },
  { id: 35033, name: "Mora County, NM" },
  { id: 35035, name: "Otero County, NM" },
  { id: 35037, name: "Quay County, NM" },
  { id: 35039, name: "Rio Arriba County, NM" },
  { id: 35041, name: "Roosevelt County, NM" },
  { id: 35043, name: "Sandoval County, NM" },
  { id: 35045, name: "San Juan County, NM" },
  { id: 35047, name: "San Miguel County, NM" },
  { id: 35049, name: "Santa Fe County, NM" },
  { id: 35051, name: "Sierra County, NM" },
  { id: 35053, name: "Socorro County, NM" },
  { id: 35055, name: "Taos County, NM" },
  { id: 35057, name: "Torrance County, NM" },
  { id: 35059, name: "Union County, NM" },
  { id: 35061, name: "Valencia County, NM" },
  { id: 36001, name: "Albany County, NY" },
  { id: 36003, name: "Allegany County, NY" },
  { id: 36005, name: "Bronx County, NY" },
  { id: 36007, name: "Broome County, NY" },
  { id: 36009, name: "Cattaraugus County, NY" },
  { id: 36011, name: "Cayuga County, NY" },
  { id: 36013, name: "Chautauqua County, NY" },
  { id: 36015, name: "Chemung County, NY" },
  { id: 36017, name: "Chenango County, NY" },
  { id: 36019, name: "Clinton County, NY" },
  { id: 36021, name: "Columbia County, NY" },
  { id: 36023, name: "Cortland County, NY" },
  { id: 36025, name: "Delaware County, NY" },
  { id: 36027, name: "Dutchess County, NY" },
  { id: 36029, name: "Erie County, NY" },
  { id: 36031, name: "Essex County, NY" },
  { id: 36033, name: "Franklin County, NY" },
  { id: 36035, name: "Fulton County, NY" },
  { id: 36037, name: "Genesee County, NY" },
  { id: 36039, name: "Greene County, NY" },
  { id: 36041, name: "Hamilton County, NY" },
  { id: 36043, name: "Herkimer County, NY" },
  { id: 36045, name: "Jefferson County, NY" },
  { id: 36047, name: "Kings County, NY" },
  { id: 36049, name: "Lewis County, NY" },
  { id: 36051, name: "Livingston County, NY" },
  { id: 36053, name: "Madison County, NY" },
  { id: 36055, name: "Monroe County, NY" },
  { id: 36057, name: "Montgomery County, NY" },
  { id: 36059, name: "Nassau County, NY" },
  { id: 36061, name: "New York County, NY" },
  { id: 36063, name: "Niagara County, NY" },
  { id: 36065, name: "Oneida County, NY" },
  { id: 36067, name: "Onondaga County, NY" },
  { id: 36069, name: "Ontario County, NY" },
  { id: 36071, name: "Orange County, NY" },
  { id: 36073, name: "Orleans County, NY" },
  { id: 36075, name: "Oswego County, NY" },
  { id: 36077, name: "Otsego County, NY" },
  { id: 36079, name: "Putnam County, NY" },
  { id: 36081, name: "Queens County, NY" },
  { id: 36083, name: "Rensselaer County, NY" },
  { id: 36085, name: "Richmond County, NY" },
  { id: 36087, name: "Rockland County, NY" },
  { id: 36089, name: "St. Lawrence County, NY" },
  { id: 36091, name: "Saratoga County, NY" },
  { id: 36093, name: "Schenectady County, NY" },
  { id: 36095, name: "Schoharie County, NY" },
  { id: 36097, name: "Schuyler County, NY" },
  { id: 36099, name: "Seneca County, NY" },
  { id: 36101, name: "Steuben County, NY" },
  { id: 36103, name: "Suffolk County, NY" },
  { id: 36105, name: "Sullivan County, NY" },
  { id: 36107, name: "Tioga County, NY" },
  { id: 36109, name: "Tompkins County, NY" },
  { id: 36111, name: "Ulster County, NY" },
  { id: 36113, name: "Warren County, NY" },
  { id: 36115, name: "Washington County, NY" },
  { id: 36117, name: "Wayne County, NY" },
  { id: 36119, name: "Westchester County, NY" },
  { id: 36121, name: "Wyoming County, NY" },
  { id: 36123, name: "Yates County, NY" },
  { id: 37001, name: "Alamance County, NC" },
  { id: 37003, name: "Alexander County, NC" },
  { id: 37005, name: "Alleghany County, NC" },
  { id: 37007, name: "Anson County, NC" },
  { id: 37009, name: "Ashe County, NC" },
  { id: 37011, name: "Avery County, NC" },
  { id: 37013, name: "Beaufort County, NC" },
  { id: 37015, name: "Bertie County, NC" },
  { id: 37017, name: "Bladen County, NC" },
  { id: 37019, name: "Brunswick County, NC" },
  { id: 37021, name: "Buncombe County, NC" },
  { id: 37023, name: "Burke County, NC" },
  { id: 37025, name: "Cabarrus County, NC" },
  { id: 37027, name: "Caldwell County, NC" },
  { id: 37029, name: "Camden County, NC" },
  { id: 37031, name: "Carteret County, NC" },
  { id: 37033, name: "Caswell County, NC" },
  { id: 37035, name: "Catawba County, NC" },
  { id: 37037, name: "Chatham County, NC" },
  { id: 37039, name: "Cherokee County, NC" },
  { id: 37041, name: "Chowan County, NC" },
  { id: 37043, name: "Clay County, NC" },
  { id: 37045, name: "Cleveland County, NC" },
  { id: 37047, name: "Columbus County, NC" },
  { id: 37049, name: "Craven County, NC" },
  { id: 37051, name: "Cumberland County, NC" },
  { id: 37053, name: "Currituck County, NC" },
  { id: 37055, name: "Dare County, NC" },
  { id: 37057, name: "Davidson County, NC" },
  { id: 37059, name: "Davie County, NC" },
  { id: 37061, name: "Duplin County, NC" },
  { id: 37063, name: "Durham County, NC" },
  { id: 37065, name: "Edgecombe County, NC" },
  { id: 37067, name: "Forsyth County, NC" },
  { id: 37069, name: "Franklin County, NC" },
  { id: 37071, name: "Gaston County, NC" },
  { id: 37073, name: "Gates County, NC" },
  { id: 37075, name: "Graham County, NC" },
  { id: 37077, name: "Granville County, NC" },
  { id: 37079, name: "Greene County, NC" },
  { id: 37081, name: "Guilford County, NC" },
  { id: 37083, name: "Halifax County, NC" },
  { id: 37085, name: "Harnett County, NC" },
  { id: 37087, name: "Haywood County, NC" },
  { id: 37089, name: "Henderson County, NC" },
  { id: 37091, name: "Hertford County, NC" },
  { id: 37093, name: "Hoke County, NC" },
  { id: 37095, name: "Hyde County, NC" },
  { id: 37097, name: "Iredell County, NC" },
  { id: 37099, name: "Jackson County, NC" },
  { id: 37101, name: "Johnston County, NC" },
  { id: 37103, name: "Jones County, NC" },
  { id: 37105, name: "Lee County, NC" },
  { id: 37107, name: "Lenoir County, NC" },
  { id: 37109, name: "Lincoln County, NC" },
  { id: 37111, name: "McDowell County, NC" },
  { id: 37113, name: "Macon County, NC" },
  { id: 37115, name: "Madison County, NC" },
  { id: 37117, name: "Martin County, NC" },
  { id: 37119, name: "Mecklenburg County, NC" },
  { id: 37121, name: "Mitchell County, NC" },
  { id: 37123, name: "Montgomery County, NC" },
  { id: 37125, name: "Moore County, NC" },
  { id: 37127, name: "Nash County, NC" },
  { id: 37129, name: "New Hanover County, NC" },
  { id: 37131, name: "Northampton County, NC" },
  { id: 37133, name: "Onslow County, NC" },
  { id: 37135, name: "Orange County, NC" },
  { id: 37137, name: "Pamlico County, NC" },
  { id: 37139, name: "Pasquotank County, NC" },
  { id: 37141, name: "Pender County, NC" },
  { id: 37143, name: "Perquimans County, NC" },
  { id: 37145, name: "Person County, NC" },
  { id: 37147, name: "Pitt County, NC" },
  { id: 37149, name: "Polk County, NC" },
  { id: 37151, name: "Randolph County, NC" },
  { id: 37153, name: "Richmond County, NC" },
  { id: 37155, name: "Robeson County, NC" },
  { id: 37157, name: "Rockingham County, NC" },
  { id: 37159, name: "Rowan County, NC" },
  { id: 37161, name: "Rutherford County, NC" },
  { id: 37163, name: "Sampson County, NC" },
  { id: 37165, name: "Scotland County, NC" },
  { id: 37167, name: "Stanly County, NC" },
  { id: 37169, name: "Stokes County, NC" },
  { id: 37171, name: "Surry County, NC" },
  { id: 37173, name: "Swain County, NC" },
  { id: 37175, name: "Transylvania County, NC" },
  { id: 37177, name: "Tyrrell County, NC" },
  { id: 37179, name: "Union County, NC" },
  { id: 37181, name: "Vance County, NC" },
  { id: 37183, name: "Wake County, NC" },
  { id: 37185, name: "Warren County, NC" },
  { id: 37187, name: "Washington County, NC" },
  { id: 37189, name: "Watauga County, NC" },
  { id: 37191, name: "Wayne County, NC" },
  { id: 37193, name: "Wilkes County, NC" },
  { id: 37195, name: "Wilson County, NC" },
  { id: 37197, name: "Yadkin County, NC" },
  { id: 37199, name: "Yancey County, NC" },
  { id: 38001, name: "Adams County, ND" },
  { id: 38003, name: "Barnes County, ND" },
  { id: 38005, name: "Benson County, ND" },
  { id: 38007, name: "Billings County, ND" },
  { id: 38009, name: "Bottineau County, ND" },
  { id: 38011, name: "Bowman County, ND" },
  { id: 38013, name: "Burke County, ND" },
  { id: 38015, name: "Burleigh County, ND" },
  { id: 38017, name: "Cass County, ND" },
  { id: 38019, name: "Cavalier County, ND" },
  { id: 38021, name: "Dickey County, ND" },
  { id: 38023, name: "Divide County, ND" },
  { id: 38025, name: "Dunn County, ND" },
  { id: 38027, name: "Eddy County, ND" },
  { id: 38029, name: "Emmons County, ND" },
  { id: 38031, name: "Foster County, ND" },
  { id: 38033, name: "Golden Valley County, ND" },
  { id: 38035, name: "Grand Forks County, ND" },
  { id: 38037, name: "Grant County, ND" },
  { id: 38039, name: "Griggs County, ND" },
  { id: 38041, name: "Hettinger County, ND" },
  { id: 38043, name: "Kidder County, ND" },
  { id: 38045, name: "LaMoure County, ND" },
  { id: 38047, name: "Logan County, ND" },
  { id: 38049, name: "McHenry County, ND" },
  { id: 38051, name: "McIntosh County, ND" },
  { id: 38053, name: "McKenzie County, ND" },
  { id: 38055, name: "McLean County, ND" },
  { id: 38057, name: "Mercer County, ND" },
  { id: 38059, name: "Morton County, ND" },
  { id: 38061, name: "Mountrail County, ND" },
  { id: 38063, name: "Nelson County, ND" },
  { id: 38065, name: "Oliver County, ND" },
  { id: 38067, name: "Pembina County, ND" },
  { id: 38069, name: "Pierce County, ND" },
  { id: 38071, name: "Ramsey County, ND" },
  { id: 38073, name: "Ransom County, ND" },
  { id: 38075, name: "Renville County, ND" },
  { id: 38077, name: "Richland County, ND" },
  { id: 38079, name: "Rolette County, ND" },
  { id: 38081, name: "Sargent County, ND" },
  { id: 38083, name: "Sheridan County, ND" },
  { id: 38085, name: "Sioux County, ND" },
  { id: 38087, name: "Slope County, ND" },
  { id: 38089, name: "Stark County, ND" },
  { id: 38091, name: "Steele County, ND" },
  { id: 38093, name: "Stutsman County, ND" },
  { id: 38095, name: "Towner County, ND" },
  { id: 38097, name: "Traill County, ND" },
  { id: 38099, name: "Walsh County, ND" },
  { id: 38101, name: "Ward County, ND" },
  { id: 38103, name: "Wells County, ND" },
  { id: 38105, name: "Williams County, ND" },
  { id: 39001, name: "Adams County, OH" },
  { id: 39003, name: "Allen County, OH" },
  { id: 39005, name: "Ashland County, OH" },
  { id: 39007, name: "Ashtabula County, OH" },
  { id: 39009, name: "Athens County, OH" },
  { id: 39011, name: "Auglaize County, OH" },
  { id: 39013, name: "Belmont County, OH" },
  { id: 39015, name: "Brown County, OH" },
  { id: 39017, name: "Butler County, OH" },
  { id: 39019, name: "Carroll County, OH" },
  { id: 39021, name: "Champaign County, OH" },
  { id: 39023, name: "Clark County, OH" },
  { id: 39025, name: "Clermont County, OH" },
  { id: 39027, name: "Clinton County, OH" },
  { id: 39029, name: "Columbiana County, OH" },
  { id: 39031, name: "Coshocton County, OH" },
  { id: 39033, name: "Crawford County, OH" },
  { id: 39035, name: "Cuyahoga County, OH" },
  { id: 39037, name: "Darke County, OH" },
  { id: 39039, name: "Defiance County, OH" },
  { id: 39041, name: "Delaware County, OH" },
  { id: 39043, name: "Erie County, OH" },
  { id: 39045, name: "Fairfield County, OH" },
  { id: 39047, name: "Fayette County, OH" },
  { id: 39049, name: "Franklin County, OH" },
  { id: 39051, name: "Fulton County, OH" },
  { id: 39053, name: "Gallia County, OH" },
  { id: 39055, name: "Geauga County, OH" },
  { id: 39057, name: "Greene County, OH" },
  { id: 39059, name: "Guernsey County, OH" },
  { id: 39061, name: "Hamilton County, OH" },
  { id: 39063, name: "Hancock County, OH" },
  { id: 39065, name: "Hardin County, OH" },
  { id: 39067, name: "Harrison County, OH" },
  { id: 39069, name: "Henry County, OH" },
  { id: 39071, name: "Highland County, OH" },
  { id: 39073, name: "Hocking County, OH" },
  { id: 39075, name: "Holmes County, OH" },
  { id: 39077, name: "Huron County, OH" },
  { id: 39079, name: "Jackson County, OH" },
  { id: 39081, name: "Jefferson County, OH" },
  { id: 39083, name: "Knox County, OH" },
  { id: 39085, name: "Lake County, OH" },
  { id: 39087, name: "Lawrence County, OH" },
  { id: 39089, name: "Licking County, OH" },
  { id: 39091, name: "Logan County, OH" },
  { id: 39093, name: "Lorain County, OH" },
  { id: 39095, name: "Lucas County, OH" },
  { id: 39097, name: "Madison County, OH" },
  { id: 39099, name: "Mahoning County, OH" },
  { id: 39101, name: "Marion County, OH" },
  { id: 39103, name: "Medina County, OH" },
  { id: 39105, name: "Meigs County, OH" },
  { id: 39107, name: "Mercer County, OH" },
  { id: 39109, name: "Miami County, OH" },
  { id: 39111, name: "Monroe County, OH" },
  { id: 39113, name: "Montgomery County, OH" },
  { id: 39115, name: "Morgan County, OH" },
  { id: 39117, name: "Morrow County, OH" },
  { id: 39119, name: "Muskingum County, OH" },
  { id: 39121, name: "Noble County, OH" },
  { id: 39123, name: "Ottawa County, OH" },
  { id: 39125, name: "Paulding County, OH" },
  { id: 39127, name: "Perry County, OH" },
  { id: 39129, name: "Pickaway County, OH" },
  { id: 39131, name: "Pike County, OH" },
  { id: 39133, name: "Portage County, OH" },
  { id: 39135, name: "Preble County, OH" },
  { id: 39137, name: "Putnam County, OH" },
  { id: 39139, name: "Richland County, OH" },
  { id: 39141, name: "Ross County, OH" },
  { id: 39143, name: "Sandusky County, OH" },
  { id: 39145, name: "Scioto County, OH" },
  { id: 39147, name: "Seneca County, OH" },
  { id: 39149, name: "Shelby County, OH" },
  { id: 39151, name: "Stark County, OH" },
  { id: 39153, name: "Summit County, OH" },
  { id: 39155, name: "Trumbull County, OH" },
  { id: 39157, name: "Tuscarawas County, OH" },
  { id: 39159, name: "Union County, OH" },
  { id: 39161, name: "Van Wert County, OH" },
  { id: 39163, name: "Vinton County, OH" },
  { id: 39165, name: "Warren County, OH" },
  { id: 39167, name: "Washington County, OH" },
  { id: 39169, name: "Wayne County, OH" },
  { id: 39171, name: "Williams County, OH" },
  { id: 39173, name: "Wood County, OH" },
  { id: 39175, name: "Wyandot County, OH" },
  { id: 40001, name: "Adair County, OK" },
  { id: 40003, name: "Alfalfa County, OK" },
  { id: 40005, name: "Atoka County, OK" },
  { id: 40007, name: "Beaver County, OK" },
  { id: 40009, name: "Beckham County, OK" },
  { id: 40011, name: "Blaine County, OK" },
  { id: 40013, name: "Bryan County, OK" },
  { id: 40015, name: "Caddo County, OK" },
  { id: 40017, name: "Canadian County, OK" },
  { id: 40019, name: "Carter County, OK" },
  { id: 40021, name: "Cherokee County, OK" },
  { id: 40023, name: "Choctaw County, OK" },
  { id: 40025, name: "Cimarron County, OK" },
  { id: 40027, name: "Cleveland County, OK" },
  { id: 40029, name: "Coal County, OK" },
  { id: 40031, name: "Comanche County, OK" },
  { id: 40033, name: "Cotton County, OK" },
  { id: 40035, name: "Craig County, OK" },
  { id: 40037, name: "Creek County, OK" },
  { id: 40039, name: "Custer County, OK" },
  { id: 40041, name: "Delaware County, OK" },
  { id: 40043, name: "Dewey County, OK" },
  { id: 40045, name: "Ellis County, OK" },
  { id: 40047, name: "Garfield County, OK" },
  { id: 40049, name: "Garvin County, OK" },
  { id: 40051, name: "Grady County, OK" },
  { id: 40053, name: "Grant County, OK" },
  { id: 40055, name: "Greer County, OK" },
  { id: 40057, name: "Harmon County, OK" },
  { id: 40059, name: "Harper County, OK" },
  { id: 40061, name: "Haskell County, OK" },
  { id: 40063, name: "Hughes County, OK" },
  { id: 40065, name: "Jackson County, OK" },
  { id: 40067, name: "Jefferson County, OK" },
  { id: 40069, name: "Johnston County, OK" },
  { id: 40071, name: "Kay County, OK" },
  { id: 40073, name: "Kingfisher County, OK" },
  { id: 40075, name: "Kiowa County, OK" },
  { id: 40077, name: "Latimer County, OK" },
  { id: 40079, name: "Le Flore County, OK" },
  { id: 40081, name: "Lincoln County, OK" },
  { id: 40083, name: "Logan County, OK" },
  { id: 40085, name: "Love County, OK" },
  { id: 40087, name: "McClain County, OK" },
  { id: 40089, name: "McCurtain County, OK" },
  { id: 40091, name: "McIntosh County, OK" },
  { id: 40093, name: "Major County, OK" },
  { id: 40095, name: "Marshall County, OK" },
  { id: 40097, name: "Mayes County, OK" },
  { id: 40099, name: "Murray County, OK" },
  { id: 40101, name: "Muskogee County, OK" },
  { id: 40103, name: "Noble County, OK" },
  { id: 40105, name: "Nowata County, OK" },
  { id: 40107, name: "Okfuskee County, OK" },
  { id: 40109, name: "Oklahoma County, OK" },
  { id: 40111, name: "Okmulgee County, OK" },
  { id: 40113, name: "Osage County, OK" },
  { id: 40115, name: "Ottawa County, OK" },
  { id: 40117, name: "Pawnee County, OK" },
  { id: 40119, name: "Payne County, OK" },
  { id: 40121, name: "Pittsburg County, OK" },
  { id: 40123, name: "Pontotoc County, OK" },
  { id: 40125, name: "Pottawatomie County, OK" },
  { id: 40127, name: "Pushmataha County, OK" },
  { id: 40129, name: "Roger Mills County, OK" },
  { id: 40131, name: "Rogers County, OK" },
  { id: 40133, name: "Seminole County, OK" },
  { id: 40135, name: "Sequoyah County, OK" },
  { id: 40137, name: "Stephens County, OK" },
  { id: 40139, name: "Texas County, OK" },
  { id: 40141, name: "Tillman County, OK" },
  { id: 40143, name: "Tulsa County, OK" },
  { id: 40145, name: "Wagoner County, OK" },
  { id: 40147, name: "Washington County, OK" },
  { id: 40149, name: "Washita County, OK" },
  { id: 40151, name: "Woods County, OK" },
  { id: 40153, name: "Woodward County, OK" },
  { id: 41001, name: "Baker County, OR" },
  { id: 41003, name: "Benton County, OR" },
  { id: 41005, name: "Clackamas County, OR" },
  { id: 41007, name: "Clatsop County, OR" },
  { id: 41009, name: "Columbia County, OR" },
  { id: 41011, name: "Coos County, OR" },
  { id: 41013, name: "Crook County, OR" },
  { id: 41015, name: "Curry County, OR" },
  { id: 41017, name: "Deschutes County, OR" },
  { id: 41019, name: "Douglas County, OR" },
  { id: 41021, name: "Gilliam County, OR" },
  { id: 41023, name: "Grant County, OR" },
  { id: 41025, name: "Harney County, OR" },
  { id: 41027, name: "Hood River County, OR" },
  { id: 41029, name: "Jackson County, OR" },
  { id: 41031, name: "Jefferson County, OR" },
  { id: 41033, name: "Josephine County, OR" },
  { id: 41035, name: "Klamath County, OR" },
  { id: 41037, name: "Lake County, OR" },
  { id: 41039, name: "Lane County, OR" },
  { id: 41041, name: "Lincoln County, OR" },
  { id: 41043, name: "Linn County, OR" },
  { id: 41045, name: "Malheur County, OR" },
  { id: 41047, name: "Marion County, OR" },
  { id: 41049, name: "Morrow County, OR" },
  { id: 41051, name: "Multnomah County, OR" },
  { id: 41053, name: "Polk County, OR" },
  { id: 41055, name: "Sherman County, OR" },
  { id: 41057, name: "Tillamook County, OR" },
  { id: 41059, name: "Umatilla County, OR" },
  { id: 41061, name: "Union County, OR" },
  { id: 41063, name: "Wallowa County, OR" },
  { id: 41065, name: "Wasco County, OR" },
  { id: 41067, name: "Washington County, OR" },
  { id: 41069, name: "Wheeler County, OR" },
  { id: 41071, name: "Yamhill County, OR" },
  { id: 42001, name: "Adams County, PA" },
  { id: 42003, name: "Allegheny County, PA" },
  { id: 42005, name: "Armstrong County, PA" },
  { id: 42007, name: "Beaver County, PA" },
  { id: 42009, name: "Bedford County, PA" },
  { id: 42011, name: "Berks County, PA" },
  { id: 42013, name: "Blair County, PA" },
  { id: 42015, name: "Bradford County, PA" },
  { id: 42017, name: "Bucks County, PA" },
  { id: 42019, name: "Butler County, PA" },
  { id: 42021, name: "Cambria County, PA" },
  { id: 42023, name: "Cameron County, PA" },
  { id: 42025, name: "Carbon County, PA" },
  { id: 42027, name: "Centre County, PA" },
  { id: 42029, name: "Chester County, PA" },
  { id: 42031, name: "Clarion County, PA" },
  { id: 42033, name: "Clearfield County, PA" },
  { id: 42035, name: "Clinton County, PA" },
  { id: 42037, name: "Columbia County, PA" },
  { id: 42039, name: "Crawford County, PA" },
  { id: 42041, name: "Cumberland County, PA" },
  { id: 42043, name: "Dauphin County, PA" },
  { id: 42045, name: "Delaware County, PA" },
  { id: 42047, name: "Elk County, PA" },
  { id: 42049, name: "Erie County, PA" },
  { id: 42051, name: "Fayette County, PA" },
  { id: 42053, name: "Forest County, PA" },
  { id: 42055, name: "Franklin County, PA" },
  { id: 42057, name: "Fulton County, PA" },
  { id: 42059, name: "Greene County, PA" },
  { id: 42061, name: "Huntingdon County, PA" },
  { id: 42063, name: "Indiana County, PA" },
  { id: 42065, name: "Jefferson County, PA" },
  { id: 42067, name: "Juniata County, PA" },
  { id: 42069, name: "Lackawanna County, PA" },
  { id: 42071, name: "Lancaster County, PA" },
  { id: 42073, name: "Lawrence County, PA" },
  { id: 42075, name: "Lebanon County, PA" },
  { id: 42077, name: "Lehigh County, PA" },
  { id: 42079, name: "Luzerne County, PA" },
  { id: 42081, name: "Lycoming County, PA" },
  { id: 42083, name: "McKean County, PA" },
  { id: 42085, name: "Mercer County, PA" },
  { id: 42087, name: "Mifflin County, PA" },
  { id: 42089, name: "Monroe County, PA" },
  { id: 42091, name: "Montgomery County, PA" },
  { id: 42093, name: "Montour County, PA" },
  { id: 42095, name: "Northampton County, PA" },
  { id: 42097, name: "Northumberland County, PA" },
  { id: 42099, name: "Perry County, PA" },
  { id: 42101, name: "Philadelphia County/city, PA" },
  { id: 42103, name: "Pike County, PA" },
  { id: 42105, name: "Potter County, PA" },
  { id: 42107, name: "Schuylkill County, PA" },
  { id: 42109, name: "Snyder County, PA" },
  { id: 42111, name: "Somerset County, PA" },
  { id: 42113, name: "Sullivan County, PA" },
  { id: 42115, name: "Susquehanna County, PA" },
  { id: 42117, name: "Tioga County, PA" },
  { id: 42119, name: "Union County, PA" },
  { id: 42121, name: "Venango County, PA" },
  { id: 42123, name: "Warren County, PA" },
  { id: 42125, name: "Washington County, PA" },
  { id: 42127, name: "Wayne County, PA" },
  { id: 42129, name: "Westmoreland County, PA" },
  { id: 42131, name: "Wyoming County, PA" },
  { id: 42133, name: "York County, PA" },
  { id: 44001, name: "Bristol County, RI" },
  { id: 44003, name: "Kent County, RI" },
  { id: 44005, name: "Newport County, RI" },
  { id: 44007, name: "Providence County, RI" },
  { id: 44009, name: "Washington County, RI" },
  { id: 45001, name: "Abbeville County, SC" },
  { id: 45003, name: "Aiken County, SC" },
  { id: 45005, name: "Allendale County, SC" },
  { id: 45007, name: "Anderson County, SC" },
  { id: 45009, name: "Bamberg County, SC" },
  { id: 45011, name: "Barnwell County, SC" },
  { id: 45013, name: "Beaufort County, SC" },
  { id: 45015, name: "Berkeley County, SC" },
  { id: 45017, name: "Calhoun County, SC" },
  { id: 45019, name: "Charleston County, SC" },
  { id: 45021, name: "Cherokee County, SC" },
  { id: 45023, name: "Chester County, SC" },
  { id: 45025, name: "Chesterfield County, SC" },
  { id: 45027, name: "Clarendon County, SC" },
  { id: 45029, name: "Colleton County, SC" },
  { id: 45031, name: "Darlington County, SC" },
  { id: 45033, name: "Dillon County, SC" },
  { id: 45035, name: "Dorchester County, SC" },
  { id: 45037, name: "Edgefield County, SC" },
  { id: 45039, name: "Fairfield County, SC" },
  { id: 45041, name: "Florence County, SC" },
  { id: 45043, name: "Georgetown County, SC" },
  { id: 45045, name: "Greenville County, SC" },
  { id: 45047, name: "Greenwood County, SC" },
  { id: 45049, name: "Hampton County, SC" },
  { id: 45051, name: "Horry County, SC" },
  { id: 45053, name: "Jasper County, SC" },
  { id: 45055, name: "Kershaw County, SC" },
  { id: 45057, name: "Lancaster County, SC" },
  { id: 45059, name: "Laurens County, SC" },
  { id: 45061, name: "Lee County, SC" },
  { id: 45063, name: "Lexington County, SC" },
  { id: 45065, name: "McCormick County, SC" },
  { id: 45067, name: "Marion County, SC" },
  { id: 45069, name: "Marlboro County, SC" },
  { id: 45071, name: "Newberry County, SC" },
  { id: 45073, name: "Oconee County, SC" },
  { id: 45075, name: "Orangeburg County, SC" },
  { id: 45077, name: "Pickens County, SC" },
  { id: 45079, name: "Richland County, SC" },
  { id: 45081, name: "Saluda County, SC" },
  { id: 45083, name: "Spartanburg County, SC" },
  { id: 45085, name: "Sumter County, SC" },
  { id: 45087, name: "Union County, SC" },
  { id: 45089, name: "Williamsburg County, SC" },
  { id: 45091, name: "York County, SC" },
  { id: 46003, name: "Aurora County, SD" },
  { id: 46005, name: "Beadle County, SD" },
  { id: 46007, name: "Bennett County, SD" },
  { id: 46009, name: "Bon Homme County, SD" },
  { id: 46011, name: "Brookings County, SD" },
  { id: 46013, name: "Brown County, SD" },
  { id: 46015, name: "Brule County, SD" },
  { id: 46017, name: "Buffalo County, SD" },
  { id: 46019, name: "Butte County, SD" },
  { id: 46021, name: "Campbell County, SD" },
  { id: 46023, name: "Charles Mix County, SD" },
  { id: 46025, name: "Clark County, SD" },
  { id: 46027, name: "Clay County, SD" },
  { id: 46029, name: "Codington County, SD" },
  { id: 46031, name: "Corson County, SD" },
  { id: 46033, name: "Custer County, SD" },
  { id: 46035, name: "Davison County, SD" },
  { id: 46037, name: "Day County, SD" },
  { id: 46039, name: "Deuel County, SD" },
  { id: 46041, name: "Dewey County, SD" },
  { id: 46043, name: "Douglas County, SD" },
  { id: 46045, name: "Edmunds County, SD" },
  { id: 46047, name: "Fall River County, SD" },
  { id: 46049, name: "Faulk County, SD" },
  { id: 46051, name: "Grant County, SD" },
  { id: 46053, name: "Gregory County, SD" },
  { id: 46055, name: "Haakon County, SD" },
  { id: 46057, name: "Hamlin County, SD" },
  { id: 46059, name: "Hand County, SD" },
  { id: 46061, name: "Hanson County, SD" },
  { id: 46063, name: "Harding County, SD" },
  { id: 46065, name: "Hughes County, SD" },
  { id: 46067, name: "Hutchinson County, SD" },
  { id: 46069, name: "Hyde County, SD" },
  { id: 46071, name: "Jackson County, SD" },
  { id: 46073, name: "Jerauld County, SD" },
  { id: 46075, name: "Jones County, SD" },
  { id: 46077, name: "Kingsbury County, SD" },
  { id: 46079, name: "Lake County, SD" },
  { id: 46081, name: "Lawrence County, SD" },
  { id: 46083, name: "Lincoln County, SD" },
  { id: 46085, name: "Lyman County, SD" },
  { id: 46087, name: "McCook County, SD" },
  { id: 46089, name: "McPherson County, SD" },
  { id: 46091, name: "Marshall County, SD" },
  { id: 46093, name: "Meade County, SD" },
  { id: 46095, name: "Mellette County, SD" },
  { id: 46097, name: "Miner County, SD" },
  { id: 46099, name: "Minnehaha County, SD" },
  { id: 46101, name: "Moody County, SD" },
  { id: 46102, name: "Oglala Lakota County, SD" },
  { id: 46103, name: "Pennington County, SD" },
  { id: 46105, name: "Perkins County, SD" },
  { id: 46107, name: "Potter County, SD" },
  { id: 46109, name: "Roberts County, SD" },
  { id: 46111, name: "Sanborn County, SD" },
  { id: 46115, name: "Spink County, SD" },
  { id: 46117, name: "Stanley County, SD" },
  { id: 46119, name: "Sully County, SD" },
  { id: 46121, name: "Todd County, SD" },
  { id: 46123, name: "Tripp County, SD" },
  { id: 46125, name: "Turner County, SD" },
  { id: 46127, name: "Union County, SD" },
  { id: 46129, name: "Walworth County, SD" },
  { id: 46135, name: "Yankton County, SD" },
  { id: 46137, name: "Ziebach County, SD" },
  { id: 47001, name: "Anderson County, TN" },
  { id: 47003, name: "Bedford County, TN" },
  { id: 47005, name: "Benton County, TN" },
  { id: 47007, name: "Bledsoe County, TN" },
  { id: 47009, name: "Blount County, TN" },
  { id: 47011, name: "Bradley County, TN" },
  { id: 47013, name: "Campbell County, TN" },
  { id: 47015, name: "Cannon County, TN" },
  { id: 47017, name: "Carroll County, TN" },
  { id: 47019, name: "Carter County, TN" },
  { id: 47021, name: "Cheatham County, TN" },
  { id: 47023, name: "Chester County, TN" },
  { id: 47025, name: "Claiborne County, TN" },
  { id: 47027, name: "Clay County, TN" },
  { id: 47029, name: "Cocke County, TN" },
  { id: 47031, name: "Coffee County, TN" },
  { id: 47033, name: "Crockett County, TN" },
  { id: 47035, name: "Cumberland County, TN" },
  { id: 47037, name: "Davidson County, TN" },
  { id: 47039, name: "Decatur County, TN" },
  { id: 47041, name: "DeKalb County, TN" },
  { id: 47043, name: "Dickson County, TN" },
  { id: 47045, name: "Dyer County, TN" },
  { id: 47047, name: "Fayette County, TN" },
  { id: 47049, name: "Fentress County, TN" },
  { id: 47051, name: "Franklin County, TN" },
  { id: 47053, name: "Gibson County, TN" },
  { id: 47055, name: "Giles County, TN" },
  { id: 47057, name: "Grainger County, TN" },
  { id: 47059, name: "Greene County, TN" },
  { id: 47061, name: "Grundy County, TN" },
  { id: 47063, name: "Hamblen County, TN" },
  { id: 47065, name: "Hamilton County, TN" },
  { id: 47067, name: "Hancock County, TN" },
  { id: 47069, name: "Hardeman County, TN" },
  { id: 47071, name: "Hardin County, TN" },
  { id: 47073, name: "Hawkins County, TN" },
  { id: 47075, name: "Haywood County, TN" },
  { id: 47077, name: "Henderson County, TN" },
  { id: 47079, name: "Henry County, TN" },
  { id: 47081, name: "Hickman County, TN" },
  { id: 47083, name: "Houston County, TN" },
  { id: 47085, name: "Humphreys County, TN" },
  { id: 47087, name: "Jackson County, TN" },
  { id: 47089, name: "Jefferson County, TN" },
  { id: 47091, name: "Johnson County, TN" },
  { id: 47093, name: "Knox County, TN" },
  { id: 47095, name: "Lake County, TN" },
  { id: 47097, name: "Lauderdale County, TN" },
  { id: 47099, name: "Lawrence County, TN" },
  { id: 47101, name: "Lewis County, TN" },
  { id: 47103, name: "Lincoln County, TN" },
  { id: 47105, name: "Loudon County, TN" },
  { id: 47107, name: "McMinn County, TN" },
  { id: 47109, name: "McNairy County, TN" },
  { id: 47111, name: "Macon County, TN" },
  { id: 47113, name: "Madison County, TN" },
  { id: 47115, name: "Marion County, TN" },
  { id: 47117, name: "Marshall County, TN" },
  { id: 47119, name: "Maury County, TN" },
  { id: 47121, name: "Meigs County, TN" },
  { id: 47123, name: "Monroe County, TN" },
  { id: 47125, name: "Montgomery County, TN" },
  { id: 47127, name: "Moore County, TN" },
  { id: 47129, name: "Morgan County, TN" },
  { id: 47131, name: "Obion County, TN" },
  { id: 47133, name: "Overton County, TN" },
  { id: 47135, name: "Perry County, TN" },
  { id: 47137, name: "Pickett County, TN" },
  { id: 47139, name: "Polk County, TN" },
  { id: 47141, name: "Putnam County, TN" },
  { id: 47143, name: "Rhea County, TN" },
  { id: 47145, name: "Roane County, TN" },
  { id: 47147, name: "Robertson County, TN" },
  { id: 47149, name: "Rutherford County, TN" },
  { id: 47151, name: "Scott County, TN" },
  { id: 47153, name: "Sequatchie County, TN" },
  { id: 47155, name: "Sevier County, TN" },
  { id: 47157, name: "Shelby County, TN" },
  { id: 47159, name: "Smith County, TN" },
  { id: 47161, name: "Stewart County, TN" },
  { id: 47163, name: "Sullivan County, TN" },
  { id: 47165, name: "Sumner County, TN" },
  { id: 47167, name: "Tipton County, TN" },
  { id: 47169, name: "Trousdale County, TN" },
  { id: 47171, name: "Unicoi County, TN" },
  { id: 47173, name: "Union County, TN" },
  { id: 47175, name: "Van Buren County, TN" },
  { id: 47177, name: "Warren County, TN" },
  { id: 47179, name: "Washington County, TN" },
  { id: 47181, name: "Wayne County, TN" },
  { id: 47183, name: "Weakley County, TN" },
  { id: 47185, name: "White County, TN" },
  { id: 47187, name: "Williamson County, TN" },
  { id: 47189, name: "Wilson County, TN" },
  { id: 48001, name: "Anderson County, TX" },
  { id: 48003, name: "Andrews County, TX" },
  { id: 48005, name: "Angelina County, TX" },
  { id: 48007, name: "Aransas County, TX" },
  { id: 48009, name: "Archer County, TX" },
  { id: 48011, name: "Armstrong County, TX" },
  { id: 48013, name: "Atascosa County, TX" },
  { id: 48015, name: "Austin County, TX" },
  { id: 48017, name: "Bailey County, TX" },
  { id: 48019, name: "Bandera County, TX" },
  { id: 48021, name: "Bastrop County, TX" },
  { id: 48023, name: "Baylor County, TX" },
  { id: 48025, name: "Bee County, TX" },
  { id: 48027, name: "Bell County, TX" },
  { id: 48029, name: "Bexar County, TX" },
  { id: 48031, name: "Blanco County, TX" },
  { id: 48033, name: "Borden County, TX" },
  { id: 48035, name: "Bosque County, TX" },
  { id: 48037, name: "Bowie County, TX" },
  { id: 48039, name: "Brazoria County, TX" },
  { id: 48041, name: "Brazos County, TX" },
  { id: 48043, name: "Brewster County, TX" },
  { id: 48045, name: "Briscoe County, TX" },
  { id: 48047, name: "Brooks County, TX" },
  { id: 48049, name: "Brown County, TX" },
  { id: 48051, name: "Burleson County, TX" },
  { id: 48053, name: "Burnet County, TX" },
  { id: 48055, name: "Caldwell County, TX" },
  { id: 48057, name: "Calhoun County, TX" },
  { id: 48059, name: "Callahan County, TX" },
  { id: 48061, name: "Cameron County, TX" },
  { id: 48063, name: "Camp County, TX" },
  { id: 48065, name: "Carson County, TX" },
  { id: 48067, name: "Cass County, TX" },
  { id: 48069, name: "Castro County, TX" },
  { id: 48071, name: "Chambers County, TX" },
  { id: 48073, name: "Cherokee County, TX" },
  { id: 48075, name: "Childress County, TX" },
  { id: 48077, name: "Clay County, TX" },
  { id: 48079, name: "Cochran County, TX" },
  { id: 48081, name: "Coke County, TX" },
  { id: 48083, name: "Coleman County, TX" },
  { id: 48085, name: "Collin County, TX" },
  { id: 48087, name: "Collingsworth County, TX" },
  { id: 48089, name: "Colorado County, TX" },
  { id: 48091, name: "Comal County, TX" },
  { id: 48093, name: "Comanche County, TX" },
  { id: 48095, name: "Concho County, TX" },
  { id: 48097, name: "Cooke County, TX" },
  { id: 48099, name: "Coryell County, TX" },
  { id: 48101, name: "Cottle County, TX" },
  { id: 48103, name: "Crane County, TX" },
  { id: 48105, name: "Crockett County, TX" },
  { id: 48107, name: "Crosby County, TX" },
  { id: 48109, name: "Culberson County, TX" },
  { id: 48111, name: "Dallam County, TX" },
  { id: 48113, name: "Dallas County, TX" },
  { id: 48115, name: "Dawson County, TX" },
  { id: 48117, name: "Deaf Smith County, TX" },
  { id: 48119, name: "Delta County, TX" },
  { id: 48121, name: "Denton County, TX" },
  { id: 48123, name: "DeWitt County, TX" },
  { id: 48125, name: "Dickens County, TX" },
  { id: 48127, name: "Dimmit County, TX" },
  { id: 48129, name: "Donley County, TX" },
  { id: 48131, name: "Duval County, TX" },
  { id: 48133, name: "Eastland County, TX" },
  { id: 48135, name: "Ector County, TX" },
  { id: 48137, name: "Edwards County, TX" },
  { id: 48139, name: "Ellis County, TX" },
  { id: 48141, name: "El Paso County, TX" },
  { id: 48143, name: "Erath County, TX" },
  { id: 48145, name: "Falls County, TX" },
  { id: 48147, name: "Fannin County, TX" },
  { id: 48149, name: "Fayette County, TX" },
  { id: 48151, name: "Fisher County, TX" },
  { id: 48153, name: "Floyd County, TX" },
  { id: 48155, name: "Foard County, TX" },
  { id: 48157, name: "Fort Bend County, TX" },
  { id: 48159, name: "Franklin County, TX" },
  { id: 48161, name: "Freestone County, TX" },
  { id: 48163, name: "Frio County, TX" },
  { id: 48165, name: "Gaines County, TX" },
  { id: 48167, name: "Galveston County, TX" },
  { id: 48169, name: "Garza County, TX" },
  { id: 48171, name: "Gillespie County, TX" },
  { id: 48173, name: "Glasscock County, TX" },
  { id: 48175, name: "Goliad County, TX" },
  { id: 48177, name: "Gonzales County, TX" },
  { id: 48179, name: "Gray County, TX" },
  { id: 48181, name: "Grayson County, TX" },
  { id: 48183, name: "Gregg County, TX" },
  { id: 48185, name: "Grimes County, TX" },
  { id: 48187, name: "Guadalupe County, TX" },
  { id: 48189, name: "Hale County, TX" },
  { id: 48191, name: "Hall County, TX" },
  { id: 48193, name: "Hamilton County, TX" },
  { id: 48195, name: "Hansford County, TX" },
  { id: 48197, name: "Hardeman County, TX" },
  { id: 48199, name: "Hardin County, TX" },
  { id: 48201, name: "Harris County, TX" },
  { id: 48203, name: "Harrison County, TX" },
  { id: 48205, name: "Hartley County, TX" },
  { id: 48207, name: "Haskell County, TX" },
  { id: 48209, name: "Hays County, TX" },
  { id: 48211, name: "Hemphill County, TX" },
  { id: 48213, name: "Henderson County, TX" },
  { id: 48215, name: "Hidalgo County, TX" },
  { id: 48217, name: "Hill County, TX" },
  { id: 48219, name: "Hockley County, TX" },
  { id: 48221, name: "Hood County, TX" },
  { id: 48223, name: "Hopkins County, TX" },
  { id: 48225, name: "Houston County, TX" },
  { id: 48227, name: "Howard County, TX" },
  { id: 48229, name: "Hudspeth County, TX" },
  { id: 48231, name: "Hunt County, TX" },
  { id: 48233, name: "Hutchinson County, TX" },
  { id: 48235, name: "Irion County, TX" },
  { id: 48237, name: "Jack County, TX" },
  { id: 48239, name: "Jackson County, TX" },
  { id: 48241, name: "Jasper County, TX" },
  { id: 48243, name: "Jeff Davis County, TX" },
  { id: 48245, name: "Jefferson County, TX" },
  { id: 48247, name: "Jim Hogg County, TX" },
  { id: 48249, name: "Jim Wells County, TX" },
  { id: 48251, name: "Johnson County, TX" },
  { id: 48253, name: "Jones County, TX" },
  { id: 48255, name: "Karnes County, TX" },
  { id: 48257, name: "Kaufman County, TX" },
  { id: 48259, name: "Kendall County, TX" },
  { id: 48261, name: "Kenedy County, TX" },
  { id: 48263, name: "Kent County, TX" },
  { id: 48265, name: "Kerr County, TX" },
  { id: 48267, name: "Kimble County, TX" },
  { id: 48269, name: "King County, TX" },
  { id: 48271, name: "Kinney County, TX" },
  { id: 48273, name: "Kleberg County, TX" },
  { id: 48275, name: "Knox County, TX" },
  { id: 48277, name: "Lamar County, TX" },
  { id: 48279, name: "Lamb County, TX" },
  { id: 48281, name: "Lampasas County, TX" },
  { id: 48283, name: "La Salle County, TX" },
  { id: 48285, name: "Lavaca County, TX" },
  { id: 48287, name: "Lee County, TX" },
  { id: 48289, name: "Leon County, TX" },
  { id: 48291, name: "Liberty County, TX" },
  { id: 48293, name: "Limestone County, TX" },
  { id: 48295, name: "Lipscomb County, TX" },
  { id: 48297, name: "Live Oak County, TX" },
  { id: 48299, name: "Llano County, TX" },
  { id: 48301, name: "Loving County, TX" },
  { id: 48303, name: "Lubbock County, TX" },
  { id: 48305, name: "Lynn County, TX" },
  { id: 48307, name: "McCulloch County, TX" },
  { id: 48309, name: "McLennan County, TX" },
  { id: 48311, name: "McMullen County, TX" },
  { id: 48313, name: "Madison County, TX" },
  { id: 48315, name: "Marion County, TX" },
  { id: 48317, name: "Martin County, TX" },
  { id: 48319, name: "Mason County, TX" },
  { id: 48321, name: "Matagorda County, TX" },
  { id: 48323, name: "Maverick County, TX" },
  { id: 48325, name: "Medina County, TX" },
  { id: 48327, name: "Menard County, TX" },
  { id: 48329, name: "Midland County, TX" },
  { id: 48331, name: "Milam County, TX" },
  { id: 48333, name: "Mills County, TX" },
  { id: 48335, name: "Mitchell County, TX" },
  { id: 48337, name: "Montague County, TX" },
  { id: 48339, name: "Montgomery County, TX" },
  { id: 48341, name: "Moore County, TX" },
  { id: 48343, name: "Morris County, TX" },
  { id: 48345, name: "Motley County, TX" },
  { id: 48347, name: "Nacogdoches County, TX" },
  { id: 48349, name: "Navarro County, TX" },
  { id: 48351, name: "Newton County, TX" },
  { id: 48353, name: "Nolan County, TX" },
  { id: 48355, name: "Nueces County, TX" },
  { id: 48357, name: "Ochiltree County, TX" },
  { id: 48359, name: "Oldham County, TX" },
  { id: 48361, name: "Orange County, TX" },
  { id: 48363, name: "Palo Pinto County, TX" },
  { id: 48365, name: "Panola County, TX" },
  { id: 48367, name: "Parker County, TX" },
  { id: 48369, name: "Parmer County, TX" },
  { id: 48371, name: "Pecos County, TX" },
  { id: 48373, name: "Polk County, TX" },
  { id: 48375, name: "Potter County, TX" },
  { id: 48377, name: "Presidio County, TX" },
  { id: 48379, name: "Rains County, TX" },
  { id: 48381, name: "Randall County, TX" },
  { id: 48383, name: "Reagan County, TX" },
  { id: 48385, name: "Real County, TX" },
  { id: 48387, name: "Red River County, TX" },
  { id: 48389, name: "Reeves County, TX" },
  { id: 48391, name: "Refugio County, TX" },
  { id: 48393, name: "Roberts County, TX" },
  { id: 48395, name: "Robertson County, TX" },
  { id: 48397, name: "Rockwall County, TX" },
  { id: 48399, name: "Runnels County, TX" },
  { id: 48401, name: "Rusk County, TX" },
  { id: 48403, name: "Sabine County, TX" },
  { id: 48405, name: "San Augustine County, TX" },
  { id: 48407, name: "San Jacinto County, TX" },
  { id: 48409, name: "San Patricio County, TX" },
  { id: 48411, name: "San Saba County, TX" },
  { id: 48413, name: "Schleicher County, TX" },
  { id: 48415, name: "Scurry County, TX" },
  { id: 48417, name: "Shackelford County, TX" },
  { id: 48419, name: "Shelby County, TX" },
  { id: 48421, name: "Sherman County, TX" },
  { id: 48423, name: "Smith County, TX" },
  { id: 48425, name: "Somervell County, TX" },
  { id: 48427, name: "Starr County, TX" },
  { id: 48429, name: "Stephens County, TX" },
  { id: 48431, name: "Sterling County, TX" },
  { id: 48433, name: "Stonewall County, TX" },
  { id: 48435, name: "Sutton County, TX" },
  { id: 48437, name: "Swisher County, TX" },
  { id: 48439, name: "Tarrant County, TX" },
  { id: 48441, name: "Taylor County, TX" },
  { id: 48443, name: "Terrell County, TX" },
  { id: 48445, name: "Terry County, TX" },
  { id: 48447, name: "Throckmorton County, TX" },
  { id: 48449, name: "Titus County, TX" },
  { id: 48451, name: "Tom Green County, TX" },
  { id: 48453, name: "Travis County, TX" },
  { id: 48455, name: "Trinity County, TX" },
  { id: 48457, name: "Tyler County, TX" },
  { id: 48459, name: "Upshur County, TX" },
  { id: 48461, name: "Upton County, TX" },
  { id: 48463, name: "Uvalde County, TX" },
  { id: 48465, name: "Val Verde County, TX" },
  { id: 48467, name: "Van Zandt County, TX" },
  { id: 48469, name: "Victoria County, TX" },
  { id: 48471, name: "Walker County, TX" },
  { id: 48473, name: "Waller County, TX" },
  { id: 48475, name: "Ward County, TX" },
  { id: 48477, name: "Washington County, TX" },
  { id: 48479, name: "Webb County, TX" },
  { id: 48481, name: "Wharton County, TX" },
  { id: 48483, name: "Wheeler County, TX" },
  { id: 48485, name: "Wichita County, TX" },
  { id: 48487, name: "Wilbarger County, TX" },
  { id: 48489, name: "Willacy County, TX" },
  { id: 48491, name: "Williamson County, TX" },
  { id: 48493, name: "Wilson County, TX" },
  { id: 48495, name: "Winkler County, TX" },
  { id: 48497, name: "Wise County, TX" },
  { id: 48499, name: "Wood County, TX" },
  { id: 48501, name: "Yoakum County, TX" },
  { id: 48503, name: "Young County, TX" },
  { id: 48505, name: "Zapata County, TX" },
  { id: 48507, name: "Zavala County, TX" },
  { id: 49001, name: "Beaver County, UT" },
  { id: 49003, name: "Box Elder County, UT" },
  { id: 49005, name: "Cache County, UT" },
  { id: 49007, name: "Carbon County, UT" },
  { id: 49009, name: "Daggett County, UT" },
  { id: 49011, name: "Davis County, UT" },
  { id: 49013, name: "Duchesne County, UT" },
  { id: 49015, name: "Emery County, UT" },
  { id: 49017, name: "Garfield County, UT" },
  { id: 49019, name: "Grand County, UT" },
  { id: 49021, name: "Iron County, UT" },
  { id: 49023, name: "Juab County, UT" },
  { id: 49025, name: "Kane County, UT" },
  { id: 49027, name: "Millard County, UT" },
  { id: 49029, name: "Morgan County, UT" },
  { id: 49031, name: "Piute County, UT" },
  { id: 49033, name: "Rich County, UT" },
  { id: 49035, name: "Salt Lake County, UT" },
  { id: 49037, name: "San Juan County, UT" },
  { id: 49039, name: "Sanpete County, UT" },
  { id: 49041, name: "Sevier County, UT" },
  { id: 49043, name: "Summit County, UT" },
  { id: 49045, name: "Tooele County, UT" },
  { id: 49047, name: "Uintah County, UT" },
  { id: 49049, name: "Utah County, UT" },
  { id: 49051, name: "Wasatch County, UT" },
  { id: 49053, name: "Washington County, UT" },
  { id: 49055, name: "Wayne County, UT" },
  { id: 49057, name: "Weber County, UT" },
  { id: 50001, name: "Addison County, VT" },
  { id: 50003, name: "Bennington County, VT" },
  { id: 50005, name: "Caledonia County, VT" },
  { id: 50007, name: "Chittenden County, VT" },
  { id: 50009, name: "Essex County, VT" },
  { id: 50011, name: "Franklin County, VT" },
  { id: 50013, name: "Grand Isle County, VT" },
  { id: 50015, name: "Lamoille County, VT" },
  { id: 50017, name: "Orange County, VT" },
  { id: 50019, name: "Orleans County, VT" },
  { id: 50021, name: "Rutland County, VT" },
  { id: 50023, name: "Washington County, VT" },
  { id: 50025, name: "Windham County, VT" },
  { id: 50027, name: "Windsor County, VT" },
  { id: 51001, name: "Accomack County, VA" },
  { id: 51003, name: "Albemarle County, VA" },
  { id: 51005, name: "Alleghany County, VA" },
  { id: 51007, name: "Amelia County, VA" },
  { id: 51009, name: "Amherst County, VA" },
  { id: 51011, name: "Appomattox County, VA" },
  { id: 51013, name: "Arlington County, VA" },
  { id: 51015, name: "Augusta County, VA" },
  { id: 51017, name: "Bath County, VA" },
  { id: 51019, name: "Bedford County, VA" },
  { id: 51021, name: "Bland County, VA" },
  { id: 51023, name: "Botetourt County, VA" },
  { id: 51025, name: "Brunswick County, VA" },
  { id: 51027, name: "Buchanan County, VA" },
  { id: 51029, name: "Buckingham County, VA" },
  { id: 51031, name: "Campbell County, VA" },
  { id: 51033, name: "Caroline County, VA" },
  { id: 51035, name: "Carroll County, VA" },
  { id: 51036, name: "Charles City County, VA" },
  { id: 51037, name: "Charlotte County, VA" },
  { id: 51041, name: "Chesterfield County, VA" },
  { id: 51043, name: "Clarke County, VA" },
  { id: 51045, name: "Craig County, VA" },
  { id: 51047, name: "Culpeper County, VA" },
  { id: 51049, name: "Cumberland County, VA" },
  { id: 51051, name: "Dickenson County, VA" },
  { id: 51053, name: "Dinwiddie County, VA" },
  { id: 51057, name: "Essex County, VA" },
  { id: 51059, name: "Fairfax County, VA" },
  { id: 51061, name: "Fauquier County, VA" },
  { id: 51063, name: "Floyd County, VA" },
  { id: 51065, name: "Fluvanna County, VA" },
  { id: 51067, name: "Franklin County, VA" },
  { id: 51069, name: "Frederick County, VA" },
  { id: 51071, name: "Giles County, VA" },
  { id: 51073, name: "Gloucester County, VA" },
  { id: 51075, name: "Goochland County, VA" },
  { id: 51077, name: "Grayson County, VA" },
  { id: 51079, name: "Greene County, VA" },
  { id: 51081, name: "Greensville County, VA" },
  { id: 51083, name: "Halifax County, VA" },
  { id: 51085, name: "Hanover County, VA" },
  { id: 51087, name: "Henrico County, VA" },
  { id: 51089, name: "Henry County, VA" },
  { id: 51091, name: "Highland County, VA" },
  { id: 51093, name: "Isle of Wight County, VA" },
  { id: 51095, name: "James City County, VA" },
  { id: 51097, name: "King and Queen County, VA" },
  { id: 51099, name: "King George County, VA" },
  { id: 51101, name: "King William County, VA" },
  { id: 51103, name: "Lancaster County, VA" },
  { id: 51105, name: "Lee County, VA" },
  { id: 51107, name: "Loudoun County, VA" },
  { id: 51109, name: "Louisa County, VA" },
  { id: 51111, name: "Lunenburg County, VA" },
  { id: 51113, name: "Madison County, VA" },
  { id: 51115, name: "Mathews County, VA" },
  { id: 51117, name: "Mecklenburg County, VA" },
  { id: 51119, name: "Middlesex County, VA" },
  { id: 51121, name: "Montgomery County, VA" },
  { id: 51125, name: "Nelson County, VA" },
  { id: 51127, name: "New Kent County, VA" },
  { id: 51131, name: "Northampton County, VA" },
  { id: 51133, name: "Northumberland County, VA" },
  { id: 51135, name: "Nottoway County, VA" },
  { id: 51137, name: "Orange County, VA" },
  { id: 51139, name: "Page County, VA" },
  { id: 51141, name: "Patrick County, VA" },
  { id: 51143, name: "Pittsylvania County, VA" },
  { id: 51145, name: "Powhatan County, VA" },
  { id: 51147, name: "Prince Edward County, VA" },
  { id: 51149, name: "Prince George County, VA" },
  { id: 51153, name: "Prince William County, VA" },
  { id: 51155, name: "Pulaski County, VA" },
  { id: 51157, name: "Rappahannock County, VA" },
  { id: 51159, name: "Richmond County, VA" },
  { id: 51161, name: "Roanoke County, VA" },
  { id: 51163, name: "Rockbridge County, VA" },
  { id: 51165, name: "Rockingham County, VA" },
  { id: 51167, name: "Russell County, VA" },
  { id: 51169, name: "Scott County, VA" },
  { id: 51171, name: "Shenandoah County, VA" },
  { id: 51173, name: "Smyth County, VA" },
  { id: 51175, name: "Southampton County, VA" },
  { id: 51177, name: "Spotsylvania County, VA" },
  { id: 51179, name: "Stafford County, VA" },
  { id: 51181, name: "Surry County, VA" },
  { id: 51183, name: "Sussex County, VA" },
  { id: 51185, name: "Tazewell County, VA" },
  { id: 51187, name: "Warren County, VA" },
  { id: 51191, name: "Washington County, VA" },
  { id: 51193, name: "Westmoreland County, VA" },
  { id: 51195, name: "Wise County, VA" },
  { id: 51197, name: "Wythe County, VA" },
  { id: 51199, name: "York County, VA" },
  { id: 51510, name: "Alexandria city, VA" },
  { id: 51520, name: "Bristol city, VA" },
  { id: 51530, name: "Buena Vista city, VA" },
  { id: 51540, name: "Charlottesville city, VA" },
  { id: 51550, name: "Chesapeake city, VA" },
  { id: 51570, name: "Colonial Heights city, VA" },
  { id: 51580, name: "Covington city, VA" },
  { id: 51590, name: "Danville city, VA" },
  { id: 51595, name: "Emporia city, VA" },
  { id: 51600, name: "Fairfax city, VA" },
  { id: 51610, name: "Falls Church city, VA" },
  { id: 51620, name: "Franklin city, VA" },
  { id: 51630, name: "Fredericksburg city, VA" },
  { id: 51640, name: "Galax city, VA" },
  { id: 51650, name: "Hampton city, VA" },
  { id: 51660, name: "Harrisonburg city, VA" },
  { id: 51670, name: "Hopewell city, VA" },
  { id: 51678, name: "Lexington city, VA" },
  { id: 51680, name: "Lynchburg city, VA" },
  { id: 51683, name: "Manassas city, VA" },
  { id: 51685, name: "Manassas Park city, VA" },
  { id: 51690, name: "Martinsville city, VA" },
  { id: 51700, name: "Newport News city, VA" },
  { id: 51710, name: "Norfolk city, VA" },
  { id: 51720, name: "Norton city, VA" },
  { id: 51730, name: "Petersburg city, VA" },
  { id: 51735, name: "Poquoson city, VA" },
  { id: 51740, name: "Portsmouth city, VA" },
  { id: 51750, name: "Radford city, VA" },
  { id: 51760, name: "Richmond city, VA" },
  { id: 51770, name: "Roanoke city, VA" },
  { id: 51775, name: "Salem city, VA" },
  { id: 51790, name: "Staunton city, VA" },
  { id: 51800, name: "Suffolk city, VA" },
  { id: 51810, name: "Virginia Beach city, VA" },
  { id: 51820, name: "Waynesboro city, VA" },
  { id: 51830, name: "Williamsburg city, VA" },
  { id: 51840, name: "Winchester city, VA" },
  { id: 53001, name: "Adams County, WA" },
  { id: 53003, name: "Asotin County, WA" },
  { id: 53005, name: "Benton County, WA" },
  { id: 53007, name: "Chelan County, WA" },
  { id: 53009, name: "Clallam County, WA" },
  { id: 53011, name: "Clark County, WA" },
  { id: 53013, name: "Columbia County, WA" },
  { id: 53015, name: "Cowlitz County, WA" },
  { id: 53017, name: "Douglas County, WA" },
  { id: 53019, name: "Ferry County, WA" },
  { id: 53021, name: "Franklin County, WA" },
  { id: 53023, name: "Garfield County, WA" },
  { id: 53025, name: "Grant County, WA" },
  { id: 53027, name: "Grays Harbor County, WA" },
  { id: 53029, name: "Island County, WA" },
  { id: 53031, name: "Jefferson County, WA" },
  { id: 53033, name: "King County, WA" },
  { id: 53035, name: "Kitsap County, WA" },
  { id: 53037, name: "Kittitas County, WA" },
  { id: 53039, name: "Klickitat County, WA" },
  { id: 53041, name: "Lewis County, WA" },
  { id: 53043, name: "Lincoln County, WA" },
  { id: 53045, name: "Mason County, WA" },
  { id: 53047, name: "Okanogan County, WA" },
  { id: 53049, name: "Pacific County, WA" },
  { id: 53051, name: "Pend Oreille County, WA" },
  { id: 53053, name: "Pierce County, WA" },
  { id: 53055, name: "San Juan County, WA" },
  { id: 53057, name: "Skagit County, WA" },
  { id: 53059, name: "Skamania County, WA" },
  { id: 53061, name: "Snohomish County, WA" },
  { id: 53063, name: "Spokane County, WA" },
  { id: 53065, name: "Stevens County, WA" },
  { id: 53067, name: "Thurston County, WA" },
  { id: 53069, name: "Wahkiakum County, WA" },
  { id: 53071, name: "Walla Walla County, WA" },
  { id: 53073, name: "Whatcom County, WA" },
  { id: 53075, name: "Whitman County, WA" },
  { id: 53077, name: "Yakima County, WA" },
  { id: 54001, name: "Barbour County, WV" },
  { id: 54003, name: "Berkeley County, WV" },
  { id: 54005, name: "Boone County, WV" },
  { id: 54007, name: "Braxton County, WV" },
  { id: 54009, name: "Brooke County, WV" },
  { id: 54011, name: "Cabell County, WV" },
  { id: 54013, name: "Calhoun County, WV" },
  { id: 54015, name: "Clay County, WV" },
  { id: 54017, name: "Doddridge County, WV" },
  { id: 54019, name: "Fayette County, WV" },
  { id: 54021, name: "Gilmer County, WV" },
  { id: 54023, name: "Grant County, WV" },
  { id: 54025, name: "Greenbrier County, WV" },
  { id: 54027, name: "Hampshire County, WV" },
  { id: 54029, name: "Hancock County, WV" },
  { id: 54031, name: "Hardy County, WV" },
  { id: 54033, name: "Harrison County, WV" },
  { id: 54035, name: "Jackson County, WV" },
  { id: 54037, name: "Jefferson County, WV" },
  { id: 54039, name: "Kanawha County, WV" },
  { id: 54041, name: "Lewis County, WV" },
  { id: 54043, name: "Lincoln County, WV" },
  { id: 54045, name: "Logan County, WV" },
  { id: 54047, name: "McDowell County, WV" },
  { id: 54049, name: "Marion County, WV" },
  { id: 54051, name: "Marshall County, WV" },
  { id: 54053, name: "Mason County, WV" },
  { id: 54055, name: "Mercer County, WV" },
  { id: 54057, name: "Mineral County, WV" },
  { id: 54059, name: "Mingo County, WV" },
  { id: 54061, name: "Monongalia County, WV" },
  { id: 54063, name: "Monroe County, WV" },
  { id: 54065, name: "Morgan County, WV" },
  { id: 54067, name: "Nicholas County, WV" },
  { id: 54069, name: "Ohio County, WV" },
  { id: 54071, name: "Pendleton County, WV" },
  { id: 54073, name: "Pleasants County, WV" },
  { id: 54075, name: "Pocahontas County, WV" },
  { id: 54077, name: "Preston County, WV" },
  { id: 54079, name: "Putnam County, WV" },
  { id: 54081, name: "Raleigh County, WV" },
  { id: 54083, name: "Randolph County, WV" },
  { id: 54085, name: "Ritchie County, WV" },
  { id: 54087, name: "Roane County, WV" },
  { id: 54089, name: "Summers County, WV" },
  { id: 54091, name: "Taylor County, WV" },
  { id: 54093, name: "Tucker County, WV" },
  { id: 54095, name: "Tyler County, WV" },
  { id: 54097, name: "Upshur County, WV" },
  { id: 54099, name: "Wayne County, WV" },
  { id: 54101, name: "Webster County, WV" },
  { id: 54103, name: "Wetzel County, WV" },
  { id: 54105, name: "Wirt County, WV" },
  { id: 54107, name: "Wood County, WV" },
  { id: 54109, name: "Wyoming County, WV" },
  { id: 55001, name: "Adams County, WI" },
  { id: 55003, name: "Ashland County, WI" },
  { id: 55005, name: "Barron County, WI" },
  { id: 55007, name: "Bayfield County, WI" },
  { id: 55009, name: "Brown County, WI" },
  { id: 55011, name: "Buffalo County, WI" },
  { id: 55013, name: "Burnett County, WI" },
  { id: 55015, name: "Calumet County, WI" },
  { id: 55017, name: "Chippewa County, WI" },
  { id: 55019, name: "Clark County, WI" },
  { id: 55021, name: "Columbia County, WI" },
  { id: 55023, name: "Crawford County, WI" },
  { id: 55025, name: "Dane County, WI" },
  { id: 55027, name: "Dodge County, WI" },
  { id: 55029, name: "Door County, WI" },
  { id: 55031, name: "Douglas County, WI" },
  { id: 55033, name: "Dunn County, WI" },
  { id: 55035, name: "Eau Claire County, WI" },
  { id: 55037, name: "Florence County, WI" },
  { id: 55039, name: "Fond du Lac County, WI" },
  { id: 55041, name: "Forest County, WI" },
  { id: 55043, name: "Grant County, WI" },
  { id: 55045, name: "Green County, WI" },
  { id: 55047, name: "Green Lake County, WI" },
  { id: 55049, name: "Iowa County, WI" },
  { id: 55051, name: "Iron County, WI" },
  { id: 55053, name: "Jackson County, WI" },
  { id: 55055, name: "Jefferson County, WI" },
  { id: 55057, name: "Juneau County, WI" },
  { id: 55059, name: "Kenosha County, WI" },
  { id: 55061, name: "Kewaunee County, WI" },
  { id: 55063, name: "La Crosse County, WI" },
  { id: 55065, name: "Lafayette County, WI" },
  { id: 55067, name: "Langlade County, WI" },
  { id: 55069, name: "Lincoln County, WI" },
  { id: 55071, name: "Manitowoc County, WI" },
  { id: 55073, name: "Marathon County, WI" },
  { id: 55075, name: "Marinette County, WI" },
  { id: 55077, name: "Marquette County, WI" },
  { id: 55078, name: "Menominee County, WI" },
  { id: 55079, name: "Milwaukee County, WI" },
  { id: 55081, name: "Monroe County, WI" },
  { id: 55083, name: "Oconto County, WI" },
  { id: 55085, name: "Oneida County, WI" },
  { id: 55087, name: "Outagamie County, WI" },
  { id: 55089, name: "Ozaukee County, WI" },
  { id: 55091, name: "Pepin County, WI" },
  { id: 55093, name: "Pierce County, WI" },
  { id: 55095, name: "Polk County, WI" },
  { id: 55097, name: "Portage County, WI" },
  { id: 55099, name: "Price County, WI" },
  { id: 55101, name: "Racine County, WI" },
  { id: 55103, name: "Richland County, WI" },
  { id: 55105, name: "Rock County, WI" },
  { id: 55107, name: "Rusk County, WI" },
  { id: 55109, name: "St. Croix County, WI" },
  { id: 55111, name: "Sauk County, WI" },
  { id: 55113, name: "Sawyer County, WI" },
  { id: 55115, name: "Shawano County, WI" },
  { id: 55117, name: "Sheboygan County, WI" },
  { id: 55119, name: "Taylor County, WI" },
  { id: 55121, name: "Trempealeau County, WI" },
  { id: 55123, name: "Vernon County, WI" },
  { id: 55125, name: "Vilas County, WI" },
  { id: 55127, name: "Walworth County, WI" },
  { id: 55129, name: "Washburn County, WI" },
  { id: 55131, name: "Washington County, WI" },
  { id: 55133, name: "Waukesha County, WI" },
  { id: 55135, name: "Waupaca County, WI" },
  { id: 55137, name: "Waushara County, WI" },
  { id: 55139, name: "Winnebago County, WI" },
  { id: 55141, name: "Wood County, WI" },
  { id: 56001, name: "Albany County, WY" },
  { id: 56003, name: "Big Horn County, WY" },
  { id: 56005, name: "Campbell County, WY" },
  { id: 56007, name: "Carbon County, WY" },
  { id: 56009, name: "Converse County, WY" },
  { id: 56011, name: "Crook County, WY" },
  { id: 56013, name: "Fremont County, WY" },
  { id: 56015, name: "Goshen County, WY" },
  { id: 56017, name: "Hot Springs County, WY" },
  { id: 56019, name: "Johnson County, WY" },
  { id: 56021, name: "Laramie County, WY" },
  { id: 56023, name: "Lincoln County, WY" },
  { id: 56025, name: "Natrona County, WY" },
  { id: 56027, name: "Niobrara County, WY" },
  { id: 56029, name: "Park County, WY" },
  { id: 56031, name: "Platte County, WY" },
  { id: 56033, name: "Sheridan County, WY" },
  { id: 56035, name: "Sublette County, WY" },
  { id: 56037, name: "Sweetwater County, WY" },
  { id: 56039, name: "Teton County, WY" },
  { id: 56041, name: "Uinta County, WY" },
  { id: 56043, name: "Washakie County, WY" },
  { id: 56045, name: "Weston County, WY" },
  { id: 72001, name: "Adjuntas Municipio, PR" },
  { id: 72003, name: "Aguada Municipio, PR" },
  { id: 72005, name: "Aguadilla Municipio, PR" },
  { id: 72007, name: "Aguas Buenas Municipio, PR" },
  { id: 72009, name: "Aibonito Municipio, PR" },
  { id: 72011, name: "Anasco Municipio, PR" },
  { id: 72013, name: "Arecibo Municipio, PR" },
  { id: 72015, name: "Arroyo Municipio, PR" },
  { id: 72017, name: "Barceloneta Municipio, PR" },
  { id: 72019, name: "Barranquitas Municipio, PR" },
  { id: 72021, name: "Bayamon Municipio, PR" },
  { id: 72023, name: "Cabo Rojo Municipio, PR" },
  { id: 72025, name: "Caguas Municipio, PR" },
  { id: 72027, name: "Camuy Municipio, PR" },
  { id: 72029, name: "Canovanas Municipio, PR" },
  { id: 72031, name: "Carolina Municipio, PR" },
  { id: 72033, name: "Catano Municipio, PR" },
  { id: 72035, name: "Cayey Municipio, PR" },
  { id: 72037, name: "Ceiba Municipio, PR" },
  { id: 72039, name: "Ciales Municipio, PR" },
  { id: 72041, name: "Cidra Municipio, PR" },
  { id: 72043, name: "Coamo Municipio, PR" },
  { id: 72045, name: "Comerio Municipio, PR" },
  { id: 72047, name: "Corozal Municipio, PR" },
  { id: 72049, name: "Culebra Municipio, PR" },
  { id: 72051, name: "Dorado Municipio, PR" },
  { id: 72053, name: "Fajardo Municipio, PR" },
  { id: 72054, name: "Florida Municipio, PR" },
  { id: 72055, name: "Guanica Municipio, PR" },
  { id: 72057, name: "Guayama Municipio, PR" },
  { id: 72059, name: "Guayanilla Municipio, PR" },
  { id: 72061, name: "Guaynabo Municipio, PR" },
  { id: 72063, name: "Gurabo Municipio, PR" },
  { id: 72065, name: "Hatillo Municipio, PR" },
  { id: 72067, name: "Hormigueros Municipio, PR" },
  { id: 72069, name: "Humacao Municipio, PR" },
  { id: 72071, name: "Isabela Municipio, PR" },
  { id: 72073, name: "Jayuya Municipio, PR" },
  { id: 72075, name: "Juana Diaz Municipio, PR" },
  { id: 72077, name: "Juncos Municipio, PR" },
  { id: 72079, name: "Lajas Municipio, PR" },
  { id: 72081, name: "Lares Municipio, PR" },
  { id: 72083, name: "Las Marias Municipio, PR" },
  { id: 72085, name: "Las Piedras Municipio, PR" },
  { id: 72087, name: "Loiza Municipio, PR" },
  { id: 72089, name: "Luquillo Municipio, PR" },
  { id: 72091, name: "Manati Municipio, PR" },
  { id: 72093, name: "Maricao Municipio, PR" },
  { id: 72095, name: "Maunabo Municipio, PR" },
  { id: 72097, name: "Mayaguez Municipio, PR" },
  { id: 72099, name: "Moca Municipio, PR" },
  { id: 72101, name: "Morovis Municipio, PR" },
  { id: 72103, name: "Naguabo Municipio, PR" },
  { id: 72105, name: "Naranjito Municipio, PR" },
  { id: 72107, name: "Orocovis Municipio, PR" },
  { id: 72109, name: "Patillas Municipio, PR" },
  { id: 72111, name: "Penuelas Municipio, PR" },
  { id: 72113, name: "Ponce Municipio, PR" },
  { id: 72115, name: "Quebradillas Municipio, PR" },
  { id: 72117, name: "Rincon Municipio, PR" },
  { id: 72119, name: "Rio Grande Municipio, PR" },
  { id: 72121, name: "Sabana Grande Municipio, PR" },
  { id: 72123, name: "Salinas Municipio, PR" },
  { id: 72125, name: "San German Municipio, PR" },
  { id: 72127, name: "San Juan Municipio, PR" },
  { id: 72129, name: "San Lorenzo Municipio, PR" },
  { id: 72131, name: "San Sebastian Municipio, PR" },
  { id: 72133, name: "Santa Isabel Municipio, PR" },
  { id: 72135, name: "Toa Alta Municipio, PR" },
  { id: 72137, name: "Toa Baja Municipio, PR" },
  { id: 72139, name: "Trujillo Alto Municipio, PR" },
  { id: 72141, name: "Utuado Municipio, PR" },
  { id: 72143, name: "Vega Alta Municipio, PR" },
  { id: 72145, name: "Vega Baja Municipio, PR" },
  { id: 72147, name: "Vieques Municipio, PR" },
  { id: 72149, name: "Villalba Municipio, PR" },
  { id: 72151, name: "Yabucoa Municipio, PR" },
  { id: 72153, name: "Yauco Municipio, PR" }
  
  
  
  ];

  // Ref to store auto-generated colors for states (keyed by state abbreviation).
  const stateColorsRef = useRef({});

  // Helper function: Given a two-digit state FIPS code, use the lookup to get its abbreviation.
  // Then, return the custom color if defined; otherwise, generate and return a unique HSL color.
  const getStateColor = (stateFips) => {
    const stateAbbr = fipsToAbbr[stateFips];
    if (!stateAbbr) {
      // If no abbreviation is found, use a fallback color.
      console.warn("State abbreviation not found for FIPS: " + stateFips);
      return "gray";
    }
    if (customStateColors[stateAbbr]) {
      return customStateColors[stateAbbr];
    }
    if (!stateColorsRef.current[stateAbbr]) {
      const currentCount = Object.keys(stateColorsRef.current).length;
      const hue = Math.floor((360 / 50) * currentCount);
      stateColorsRef.current[stateAbbr] = `hsl(${hue}, 70%, 60%)`;
    }
    return stateColorsRef.current[stateAbbr];
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setShowSuggestions(true);
  };

  // Filter counties data for search suggestions.
  const filteredCounties = countiesData.filter((county) =>
    county.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (event) => {
    setSelectedCounty(event.target.value);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCounty(null);
  };

  // Load CSV data for counties.
  useEffect(() => {
    csv("/unemployment-by-county-2017.csv").then((counties) => {
      setData(counties);
    });
  }, []);

  // Fetch TopoJSON data (for states and counties).
  useEffect(() => {
    fetch(geoUrl)
      .then(response => response.json())
      .then(jsonData => setTopology(jsonData));
  }, []);

  return (
    <>
      {/* Search Box and Suggestions */}
      <div
        style={{
          width: "300px",
          margin: "0 auto",
          position: "relative",
          marginTop: "70px",
          marginBottom: "-90px"
        }}
        ref={dropdownRef}
      >
        <input
          id="county-search"
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Type to search..."
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            marginBottom: "5px"
          }}
        />
        {showSuggestions && filteredCounties.length > 0 && (
          <ul
            style={{
              listStyleType: "none",
              margin: 0,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: "4px",
              maxHeight: "150px",
              overflowY: "auto",
              backgroundColor: "#fff",
              position: "absolute",
              width: "100%",
              zIndex: 10
            }}
          >
            {filteredCounties.map((county) => (
              <li
                key={county.id}
                onClick={() => {
                  console.log(`County clicked: ${county ? county.name : "Unknown"}`);
                  setSelectedCounty(county ? county.name : "Unknown");
                  setSelectedCountyID(county ? county.id : "Unknown");
                  handleOpen();
                }}
                style={{
                  padding: "8px",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#e0e0e0")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "")
                }
              >
                {county.name}
              </li>
            ))}
          </ul>
        )}
        {selectedCounty && <p>You selected: {selectedCounty}</p>}
      </div>

      <ComposableMap data-tip="" projection="geoAlbersUsa">
        <ZoomableGroup zoom={1}>
          {/* Optional: State Fill Layer */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                // Look up the county in CSV data, if needed.
                const cur = data.find((s) => s.id === geo.id);
                // Extract the state FIPS from the county id (first two digits)
                const stateFips = geo.id.substring(0, 2);
                // Use our helper function to get the color by state abbreviation.
                const fillColor = getStateColor(stateFips);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => {
                      const countyName = cur ? cur.name : "Unknown";
                      const countyID = cur ? cur.id : "Unknown";
                      console.log(`County clicked: ${countyName}`);
                      setSelectedCounty(countyName);
                      setSelectedCountyID(countyID);
                      handleOpen();
                    }}
                    onMouseEnter={() => {
                      setTooltipContent(`${cur ? cur.name : "Unknown"}`);
                    }}
                    onMouseLeave={() => {
                      setTooltipContent("");
                    }}
                    style={{
                      default: {
                        fill: fillColor,
                        stroke: "#fff",
                        strokeWidth: 1
                      },
                      hover: {
                        fill: fillColor,
                        stroke: "#fff",
                        strokeWidth: 1
                      },
                      pressed: {
                        fill: fillColor,
                        stroke: "#fff",
                        strokeWidth: 1
                      }
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* County Layer */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const cur = data.find((s) => s.id === geo.id);
                const stateFips = geo.id.substring(0, 2);
                const fillColor = getStateColor(stateFips);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => {
                      const countyName = cur ? cur.name : "Unknown";
                      const countyID = cur ? cur.id : "Unknown";
                      console.log(`County clicked: ${countyName}`);
                      setSelectedCounty(countyName);
                      setSelectedCountyID(countyID);
                      handleOpen();
                    }}
                    onMouseEnter={() => {
                      setTooltipContent(`${cur ? cur.name : "Unknown"}`);
                    }}
                    onMouseLeave={() => {
                      setTooltipContent("");
                    }}
                    style={{
                      default: {
                        fill: fillColor,
                        stroke: "#000",
                        strokeWidth: 0.5
                      },
                      hover: {
                        fill: "white", // When hovered, the county becomes white.
                        stroke: "#000",
                        strokeWidth: 0.5
                      },
                      pressed: {
                        fill: fillColor,
                        stroke: "#000",
                        strokeWidth: 0.5
                      }
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Optional: State Border Overlay */}
          {topology && topology.objects && topology.objects.states && (
            <Geographies geography={geoUrl}>
              {({ projection }) => {
                const statesMesh = mesh(
                  topology,
                  topology.objects.states,
                  (a, b) => a !== b
                );
                return (
                  <path
                    d={geoPath(projection)(statesMesh)}
                    fill="none"
                    stroke="#fff"
                    strokeWidth={1}
                  />
                );
              }}
            </Geographies>
          )}
        </ZoomableGroup>
      </ComposableMap>

      <CountyModal
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        selectedCounty={selectedCounty}
        selectedCountyID={selectedCountyID}
      />
    </>
  );
};

export default MapChart;
